import { taxModeText } from "../../model/steuerbegriffe";
import {
  DEFAULT_TAX_CONFIG, TAX_MODE_DB, smallAmountRules, formatMoney, fxMonthConverter, normalizeCountry, resolveInvoiceLanguage, taxIdLabel,
  addDays, addMonths, approvalQuestions, checkAction, checkBudgetDeviation, checkInvoiceDate, checkIssuerProfile, checkNumberConflict,
  checkNumberMonth, checkServicePeriod, checkVatIdFormat, computeDealSplit, computeInvoiceTotals, deriveInvoiceState, detectExpenseLikeLines, determineTaxTreatment, vatRateChoices, renderTaxNoticeForRates,
  diffDays, dueDateFrom, findSameServiceDuplicates, inPaymentRun, invoiceFromRow, isCurrency, isQrIban, isValidBic, isValidIban, lastDayOfMonth, monthRange,
  normalizeInvoiceNumber, resolveCommissionRate, dealBpForMonth, talentBucket, talentPrimaryAction, taxModeLabel, taxTreatmentNeedsConfirmation,
  brandDiscountFromRow, brandNetLines, commissionCents, creatorShareLines, effectiveCommissionPercent, extraInPeriod, shareOfHonorar, tiersFromJson,
  type BrandDiscount, type CommissionTier,
  type Bucket, type Currency, type DunningPolicy, type Finding, type InvoiceNumberScheme, type InvoiceRecord, type InvoiceRow as DomainInvoiceRow,
  type InvoiceStateContext, type InvoiceStateResult, type InvoiceTotals, type IsoDate, type LineInput, type MonthKey, type PrimaryAction,
  type ProfileField, type TaxTreatment,
  DEAL_MODEL_KEYS, dealModelName, deliverableLabel, holidayDatesOrNone, nextDue, csvCellGuard,
} from "@talentkontor/domain";
import type { Pill, Tone } from "../../model/invoice-view";
import { decimalSeparator, today as heuteLokal } from "../../lib/format";
import { parseQuantityInput } from "../../lib/betrag";
import type {
  CBooking, CBrand, CContract, CDeal, CDeliverable, CInvoice, CLineItem, CPayout, CPeriod, CreatorBase, CSigner, DealRate, OrgAddress, OwnNumber, PlatformDefaults, TalentSelf,
} from "./data";
import { dateOf, monthOf, monthShortOf, numOf, pctOf, tr, type Lang } from "./lang";
import { mailLanguageName } from "./mailsprache";

/**
 * Rechenlogik des Creator-Portals, rein und ohne Datenbank: Anteil nach Provision, Kennzahlen der Übersicht,
 * Rechnungszustand aus Talent-Sicht, nächste Schritte und die Prüfungen des Rechnungs-Assistenten.
 * Fachregeln kommen aus @talentkontor/domain; hier steht nur, was die Talent-Sicht zusätzlich braucht.
 */

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
export const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

// ── Einstellungen, die ein Talent lesen darf ────────────────────────────────

/** Plattform-Standard lesen (Talents sehen die Agenturwerte nicht, S-10). */
export function setting<T>(defaults: PlatformDefaults, key: string, check: (v: unknown) => v is T): T | null {
  const v = defaults[key];
  return check(v) ? v : null;
}
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === "boolean";
const isStrList = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");
const isScheme = (v: unknown): v is InvoiceNumberScheme =>
  !!v && typeof v === "object" && ((v as { monthCheck?: unknown }).monthCheck === "off" || (v as { monthCheck?: unknown }).monthCheck === "detect");

export interface CreatorRules {
  dueSoonDays: number;
  paymentCheckCooldownDays: number;
  stillOverdueRecheckDays: number;
  talentAnswerDays: number;
  minLineDescriptionChars: number;
  invoiceDateWorkdaysOnly: boolean;
  /** Feiertagsregion der Agentur (sonst ihr Land): Rechnungsdatum nie auf einen Feiertag (SAAS-5) */
  holidayRegion: string | null;
  budgetDeviationPercent: number;
  expenseKeywords: string[];
  numberScheme: InvoiceNumberScheme;
  profileRequirements: string[];
  /** Auszahlung erst nach Zahlungseingang der Marke (talent_payout_mode, 0042); dann ist „überfällig“ ohne Markenzahlung falsch */
  payoutAfterBrandPayment: boolean;
}

/**
 * Regeln für den Creator: zuerst die Einstellung der Agentur (org_profiles, 0031), dann der Plattform-Standard, dann
 * ein fester Rückfall. So wirken die Konditionen der Agentur auch im Rechnungsassistenten des Creators.
 */
export function creatorRules(defaults: PlatformDefaults, org?: Partial<OrgAddress> | null): CreatorRules {
  const own = (org ?? {}) as Record<string, unknown>;
  const pick = <T,>(key: string, ok: (v: unknown) => v is T): T | undefined => {
    const raw = own[key];
    const v = key === "budget_deviation_percent" && typeof raw === "string" ? Number(raw) : raw;
    return ok(v) && !(Array.isArray(v) && v.length === 0) ? v : (setting(defaults, key, ok) ?? undefined);
  };
  return {
    dueSoonDays: pick("due_soon_days", isNum) ?? 7,
    paymentCheckCooldownDays: setting(defaults, "payment_check_cooldown_days", isNum) ?? 3,
    stillOverdueRecheckDays: setting(defaults, "still_overdue_recheck_days", isNum) ?? 3,
    talentAnswerDays: setting(defaults, "talent_answer_days", isNum) ?? 5,
    minLineDescriptionChars: pick("min_line_description_chars", isNum) ?? 12,
    invoiceDateWorkdaysOnly: pick("invoice_date_workdays_only", isBool) ?? false,
    holidayRegion: (typeof own.holiday_region === "string" && own.holiday_region) || (typeof own.country === "string" && own.country) || null,
    budgetDeviationPercent: pick("budget_deviation_percent", isNum) ?? 5,
    // Leere Liste der Agentur = Rückfrage aus (SAAS-R3-11); nur ohne Angabe gelten die Stichworte der Plattform
    expenseKeywords: Array.isArray(own.expense_keywords) && own.expense_keywords.length === 0 ? [] : pick("expense_keywords", isStrList) ?? [],
    numberScheme: pick("invoice_number_scheme", isScheme) ?? { monthCheck: "detect" },
    profileRequirements: pick("talent_profile_requirements", isStrList) ?? ["street", "postal_code", "city", "country", "iban", "bank_account_holder"],
    payoutAfterBrandPayment: pick("talent_payout_mode", (v): v is string => typeof v === "string") === "after_brand_payment",
  };
}

/** Kooperation mit direkter Abrechnung: der Creator stellt voll an die Marke, die Agentur ihm ihre Provision (0039). */
export const isDirect = (deal: Pick<CDeal, "invoice_route"> | null | undefined): boolean => deal?.invoice_route === "direct_to_brand";
/** CREATOR-1: Beträge der Kooperation sind schon dein Anteil (Agentur zeigt nur den Anteil, 0079); keine Provision abziehen. */
export const amountsAreShare = (deal: Pick<CDeal, "amounts_basis"> | null | undefined): boolean => deal?.amounts_basis === "share";
/** Rechnung ohne Provisionsabzug: direkt an die Marke (voller Betrag) oder Beträge schon als Anteil */
export const invoicesWithoutDeduction = (deal: Pick<CDeal, "invoice_route" | "amounts_basis"> | null | undefined): boolean => isDirect(deal) || amountsAreShare(deal);

export interface DraftRecipient {
  name: string;
  lines: string[];
  country: string | null;
  vatId: string | null;
  isBusiness: boolean;
  /** Hinweis für den Creator (Empfänger über die Agentur der Marke) */
  note: string | null;
  /** Sprache des Rechnungsbelegs (Empfängersprache, WS-03) */
  language: "de" | "en";
  /** Anschrift in Feldern (Zahlungspflichtiger im Schweizer QR-Zahlteil, 0069) */
  address?: { name: string; street: string | null; postal_code: string | null; city: string | null; country: string | null } | null;
}

/** Empfänger der Rechnung des Creators: die Agentur, bei direkter Abrechnung die Marke bzw. ihre vertretende Agentur. */
export function recipientOf(b: Pick<CreatorBase, "org" | "billing" | "brands">, deal: CDeal | null, lang: Lang = "de"): DraftRecipient {
  if (deal && isDirect(deal)) {
    const p = b.billing.get(deal.id);
    const brand = b.brands.get(deal.brand_id);
    const country = p?.country ?? brand?.country ?? null;
    return {
      name: p?.name ?? brand?.legal_name ?? brand?.name ?? tr(lang, "Marke", "Brand"),
      lines: [p?.street, [p?.postal_code, p?.city].filter(Boolean).join(" "), p?.country, p?.reference ? `${tr(lang, "Referenz", "Reference")}: ${p.reference}` : null].filter((x): x is string => !!x && !!x.trim()),
      country,
      vatId: p?.vat_id ?? null,
      // K-334: Privatperson laut Markenprofil (sonst Unternehmen)
      isBusiness: p?.business !== false,
      note: p?.on_behalf_of ? tr(lang, `Die Rechnung geht an die Agentur der Marke, für ${p.on_behalf_of}.`, `The invoice goes to the brand's agency, on behalf of ${p.on_behalf_of}.`) : null,
      language: resolveInvoiceLanguage({ recipientCountry: country }),
      address: { name: p?.name ?? brand?.legal_name ?? brand?.name ?? "", street: p?.street ?? null, postal_code: p?.postal_code ?? null, city: p?.city ?? null, country },
    };
  }
  const o = b.org;
  const own = o.default_document_language === "en" || o.default_document_language === "de" ? o.default_document_language : null;
  return {
    name: o.legal_name || o.name,
    lines: [o.street, [o.postal_code, o.city].filter(Boolean).join(" "), o.country].filter((x): x is string => !!x && !!x.trim()),
    country: o.country, vatId: o.vat_id, isBusiness: true, note: null,
    language: resolveInvoiceLanguage({ explicitLanguage: own, recipientCountry: o.country }),
    address: { name: o.legal_name || o.name, street: o.street, postal_code: o.postal_code, city: o.city, country: o.country },
  };
}

/** Die Mahnleiter der Agentur ist für Talents nicht lesbar; Rechnungen an die eigene Agentur werden nie gemahnt (WS-17). */
function emptyPolicy(currency: string): DunningPolicy {
  return { stages: [], feeCurrency: (isCurrency(currency) ? currency : "EUR") as Currency, feeMode: "cumulative", baseRates: [], surchargePercentPoints: 0, flatFeeCents: 0 };
}

export function talentStateContext(today: IsoDate, rules: CreatorRules, currency: string): InvoiceStateContext {
  return {
    today,
    policy: emptyPolicy(currency),
    holidays: [],
    settings: {
      dueSoonDays: rules.dueSoonDays,
      paymentCheckCooldownDays: rules.paymentCheckCooldownDays,
      stillOverdueRecheckDays: rules.stillOverdueRecheckDays,
      talentAnswerDays: rules.talentAnswerDays,
      // Nur für den Versand an Marken (Agentur-Aktion); dem Talent unbekannt und hier nie gebraucht.
      defaultPaymentTermDays: Number.NaN,
    },
  };
}

// ── Rechnungen aus Talent-Sicht ─────────────────────────────────────────────

export interface TalentInvoiceView {
  row: CInvoice;
  record: InvoiceRecord;
  state: InvoiceStateResult;
  pill: Pill;
  bucket: Bucket;
  primary: PrimaryAction | null;
}

export function toRecord(row: CInvoice, timeZone: string): InvoiceRecord | null {
  try {
    return invoiceFromRow(row as unknown as DomainInvoiceRow, timeZone);
  } catch {
    return null;
  }
}

export function viewTalentInvoice(row: CInvoice, ctx: InvoiceStateContext, timeZone: string, lang: Lang = "de",
  opts: { payoutAfterBrandPayment?: boolean; payoutDue?: ReadonlyMap<string, string | null> } = {}): TalentInvoiceView | null {
  let record = toRecord(row, timeZone);
  if (!record) return null;
  // Fälligkeit der Auszahlung (invoice_payout_info, 0055): im Modus „nach Zahlungseingang“ ab Zahlung der Marke plus Frist;
  // ohne Angabe und in diesem Modus gilt die Frist der Rechnung nicht (die Marke hat noch nicht gezahlt)
  if (row.kind === "talent_to_agency") {
    if (opts.payoutDue?.has(row.id)) record = { ...record, payoutDueDate: opts.payoutDue.get(row.id) ?? null };
    else if (row.payout_due !== undefined) record = { ...record, payoutDueDate: row.payout_due };
    else if (opts.payoutAfterBrandPayment) record = { ...record, payoutDueDate: null };
  }
  const state = deriveInvoiceState(record, ctx);
  // Gutschrift der Agentur: der Creator prüft sie bis zur Auszahlung (Befund Creator 11)
  // Mit „Passt so“ bestätigt (CREATOR-R3-5, 0091): keine offene Aufgabe mehr, der Widerspruch bleibt möglich
  const selfBilledToCheck = !!row.self_billed && !row.self_billing_checked_at && ["approved", "payout_overdue", "sent", "due_soon", "due_today"].includes(state.state);
  // Eine gemeldete, noch nicht bestätigte Zahlung ist offen, nicht erledigt: sie lässt sich zurücknehmen (Befund 8)
  const bucket = state.state === "payment_reported" ? "open" : selfBilledToCheck ? "action" : talentBucket(record, ctx);
  const p = selfBilledToCheck && state.state !== "payout_overdue" ? pill(tr(lang, "Gutschrift · bitte prüfen", "Self-billed invoice · please check"), "warning") : talentPill(state, row.kind, lang, !!row.self_billed);
  return { row, record, state, pill: p, bucket, primary: talentPrimaryAction(record, ctx) };
}

/** Statuspille aus Sicht des Creators (Rechnung an die eigene Agentur: „bezahlt“ heißt ausgezahlt). */
export function talentPill(s: InvoiceStateResult, kind: CInvoice["kind"], lang: Lang = "de", selfBilled = false): Pill {
  const toAgency = kind === "talent_to_agency";
  const t = (de: string, en: string) => tr(lang, de, en);
  switch (s.state) {
    case "draft": return pill(t("Entwurf", "Draft"), "neutral");
    case "in_review": return pill(t("In Prüfung", "In review"), "warning");
    case "approved": return pill(t("Freigegeben", "Approved"), "info");
    // Freigegeben, Frist vorbei, noch nicht im Zahlungslauf bzw. nicht an die Marke gesendet: die Agentur ist im Verzug (Befund 11)
    case "send_overdue": return pill(toAgency ? t("Auszahlung überfällig", "Payout overdue") : t("Versand überfällig", "Sending overdue"), "warning");
    case "payout_overdue": return pill(t("Auszahlung überfällig", "Payout overdue"), "warning");
    case "payout_confirmed": return pill(t("Ausgezahlt · bitte Eingang bestätigen", "Paid out · please confirm receipt"), "warning");
    case "delivery_pending": return pill(t("Bei der Marke", "With the brand"), "info");
    case "delivery_failed": return pill(t("Versand an die Marke wird wiederholt", "Sending to the brand is being retried"), "info");
    case "bad_debt": return pill(t("Ausgebucht", "Written off"), "neutral");
    case "sent":
    case "due_soon":
    case "due_today": return pill(toAgency ? t("Auszahlung läuft", "Payout in progress") : t("Bei der Marke", "With the brand"), "info");
    case "overdue": return pill(t("Überfällig", "Overdue"), "error");
    case "dunning": return pill(t("Gemahnt", "Reminded"), "error");
    case "payment_reported": return pill(t("Zahlung gemeldet", "Payment reported"), "success");
    case "money_missing": return pill(t("Geld fehlt", "Money missing"), "error");
    case "correction_needed": return pill(t("Korrektur nötig", "Correction needed"), "error");
    case "collection": return pill(t("Inkasso", "Debt collection"), "error");
    case "paid": return pill(toAgency ? t("Ausgezahlt", "Paid out") : t("Bezahlt", "Paid"), "success");
    case "cancelled": return pill(t("Storniert", "Cancelled"), "neutral");
    // K-460: aus Sicht des Creators ist es bei der Rechnung an die Agentur eine Rückzahlung
    case "cancelled_refund_pending": return pill(toAgency ? t("Storniert · Rückzahlung offen", "Cancelled · repayment pending") : t("Storniert · Erstattung offen", "Cancelled · refund pending"), "warning");
    case "credit_note": return pill(selfBilled ? t("Stornobeleg", "Cancellation document") : t("Stornorechnung", "Credit note"), "neutral");
    case "withdrawn": return pill(t("Zurückgezogen", "Withdrawn"), "neutral");
    case "discarded": return pill(t("Verworfen", "Discarded"), "neutral");
  }
}

/** Beschriftung der Hauptaktion (15_texte.md, Talent-Aktionen). */
export function talentActionLabel(a: PrimaryAction | null, lang: Lang = "de"): string {
  const t = (de: string, en: string) => tr(lang, de, en);
  if (!a) return t("Details ansehen", "View details");
  switch (a.labelKey) {
    case "action.submit": return t("Rechnung einreichen", "Submit invoice");
    case "action.edit_and_generate_pdf": return t("Weiter bearbeiten", "Continue editing");
    case "action.correct": return t("Rechnung korrigieren", "Correct invoice");
    case "action.withdraw_submission": return t("Einreichung zurückziehen", "Withdraw submission");
    case "action.report_paid": return t("Zahlung melden", "Report payment");
    case "action.open_pdf": return t("PDF öffnen", "Open PDF");
    // GELD-R3-1: anteilig ausgezahlt, der Rest folgt
    case "hint.payout_partial": return t("Anteilig ausgezahlt · Rest folgt", "Partly paid out · rest to follow");
    default: return t("Details ansehen", "View details");
  }
}

/** Was der Creator mit einer Rechnung tun darf: Fachlogik (checkAction) UND das, was die Datenbank zulässt. */
export interface TalentActions {
  edit: boolean;
  submit: boolean;
  discard: boolean;
  withdraw: boolean;
  /** RE-18: Einreichung zurückziehen, solange die Agentur noch nicht geprüft hat (invoice_withdraw_submission, 0031) */
  withdrawSubmission: boolean;
  /** Eingereicht und schon geprüft: zurückziehen geht nicht mehr, nur Nachricht an die Agentur */
  withdrawSubmissionTooLate: boolean;
  reportPaid: boolean;
  reportStillOverdue: boolean;
  /** Eigene Zahlungsmeldung zurücknehmen, solange die Agentur nicht gebucht hat (invoice_retract_payment_report, 0031) */
  retractPayment: boolean;
  /** Auszahlung bzw. Versand überfällig: bei der Agentur nachfragen (Befund 11) */
  askAgency: boolean;
  openPdf: boolean;
  /** Gutschrift der Agentur: widersprechen, solange die Auszahlung nicht angewiesen ist (0046/0051) */
  objectSelfBilling: boolean;
  /** Gutschrift als geprüft bestätigen („Passt so“, CREATOR-R3-5) */
  acceptSelfBilling: boolean;
}

export function talentActions(v: TalentInvoiceView, ctx: InvoiceStateContext): TalentActions {
  const actor = { role: "talent" as const, isOwnInvoice: true };
  const ok = (a: Parameters<typeof checkAction>[1]) => checkAction(v.record, a, actor, ctx).allowed;
  const status = v.row.status;
  const editableInDb = status === "draft" || status === "revision_requested" || status === "rejected";
  const submitted = status === "submitted";
  return {
    edit: ok("edit") && editableInDb,
    submit: ok("submit") && editableInDb,
    discard: ok("discard") && editableInDb,
    // Zurückziehen einer zurückgegebenen (oder in Korrektur befindlichen) Rechnung gibt die Nummer frei (Status „withdrawn“)
    withdraw: editableInDb && (status !== "draft" || !!v.row.reviewed_at),
    withdrawSubmission: submitted && !v.row.reviewed_at,
    withdrawSubmissionTooLate: submitted && !!v.row.reviewed_at,
    reportPaid: ok("report_paid"),
    reportStillOverdue: ok("report_still_overdue"),
    retractPayment: !!v.row.talent_payment_response && status !== "paid" && status !== "cancelled" && !v.row.paid_at,
    askAgency: v.state.state === "send_overdue" || v.state.state === "payout_overdue" || v.state.state === "money_missing",
    // Archiviertes PDF (0055), auch für Gutschrift und Rechnungen der Agentur an den Creator
    openPdf: !!v.row.pdf_path,
    // Auch ein schon ausgezahlter Teil sperrt den Widerspruch (GELD-R3-2, 0091)
    // 0345: der Stornobeleg zur Gutschrift (credit_for) ist kein Gegenstand von Widerspruch oder „Passt so“
    objectSelfBilling: !!v.row.self_billed && !v.row.credit_for && (status === "approved" || status === "sent") && !v.row.paid_at && !v.row.admin_payment_confirmed_at && !((v.row.paid_cents ?? 0) > 0),
    acceptSelfBilling: !!v.row.self_billed && !v.row.credit_for && (status === "approved" || status === "sent") && !v.row.paid_at && !v.row.self_billing_checked_at,
  };
}

/**
 * Anteilig ausgezahlt bzw. verrechnet (GELD-R3-1/-3, 0091): bei einer offenen Creator-Rechnung an die Agentur der schon
 * überwiesene bzw. verrechnete Betrag, sonst 0. Nach der vermerkten vollen Auszahlung oder Bezahlung ist nichts mehr anteilig.
 */
/** Noch offener Betrag einer Provisionsrechnung: brutto abzüglich Teilzahlungen und Verrechnungen. */
export const feeRest = (i: Pick<CInvoice, "gross_cents" | "paid_cents">): number => Math.max(0, i.gross_cents - Number(i.paid_cents ?? 0));

export function partialPaidOutCents(row: Pick<CInvoice, "kind" | "status" | "paid_at" | "admin_payment_confirmed_at" | "gross_cents" | "paid_cents">): number {
  const paid = Number(row.paid_cents ?? 0);
  if (row.kind !== "talent_to_agency" || !["approved", "sent"].includes(row.status) || row.paid_at || row.admin_payment_confirmed_at) return 0;
  return paid > 0 && paid < row.gross_cents ? paid : 0;
}

export interface TimelineStep {
  label: string;
  value: string;
  done: boolean;
}

/**
 * Fortschritt einer Rechnung wie im Design (rechnungsstatus.html): eingereicht → Prüfung → Freigabe → Marke → Auszahlung.
 * Direkt an die Marke: eingereicht → Prüfung → Freigabe → Zahlung der Marke (an dich).
 */
export function invoiceTimeline(v: TalentInvoiceView, period: CPeriod | undefined, fmt: (d: string | null) => string, lang: Lang = "de",
  /** Zahlung der Marke laut invoice_payout_info (0055/0076); ohne Angabe gilt der Stand des Monats */
  brandState?: string | null): TimelineStep[] {
  const t = (de: string, en: string) => tr(lang, de, en);
  const r = v.row;
  const s = v.state.state;
  // K-448: Stornobeleg zur Gutschrift: ausgestellt, keine Auszahlung
  if (r.self_billed && r.credit_for) return [
    { label: t("Stornobeleg von deiner Agentur ausgestellt", "Cancellation document issued by your agency"), value: fmt(r.approved_at ?? r.issue_date ?? r.created_at), done: true },
    { label: t("Auszahlung", "Payout"), value: t("keine", "none"), done: true },
  ];
  const submitted = !!r.submitted_at;
  const correction = s === "correction_needed" || r.status === "revision_requested" || r.status === "rejected";
  // Nach einer Rückgabe zählt eine frühere Freigabe nicht mehr (Befund 22)
  const approved = !correction && (["approved", "sent", "paid"].includes(r.status) || (!!r.approved_at && r.status !== "draft" && r.status !== "submitted"));
  const paidOut = s === "paid" || r.status === "paid";
  const direct = r.kind === "talent_to_brand";
  // Ein Forderungsausfall setzt den Monat auf „bezahlt“, Geld kam aber keins: maßgeblich ist die Zahlung der Marke (GELD-5)
  const brandPaid = paidOut || (brandState ? brandState === "paid" : period?.status === "paid");
  const inRun = inPaymentRun(v.record);
  const closed = ["withdrawn", "discarded", "cancelled"].includes(r.status);
  const open = closed ? t("entfällt", "not applicable") : t("ausstehend", "pending");
  // Gutschrift: die Agentur stellt die Rechnung selbst aus, es gibt kein Einreichen und keine Prüfung durch sie
  const steps: TimelineStep[] = r.self_billed
    ? [{ label: t("Gutschrift von deiner Agentur ausgestellt", "Self-billed invoice issued by your agency"), value: fmt(r.approved_at ?? r.issue_date ?? r.created_at), done: true }]
    : [
    { label: t("Eingereicht", "Submitted"), value: submitted ? fmt(r.submitted_at) : open, done: submitted },
    {
      label: t("Prüfung durch deine Agentur", "Review by your agency"),
      value: correction ? t("Korrektur angefordert", "Correction requested") : approved ? t("abgeschlossen", "done") : s === "in_review" ? t("läuft", "in progress") : open,
      done: approved,
    },
    { label: t("Freigegeben", "Approved"), value: approved ? fmt(r.approved_at ?? r.reviewed_at) : open, done: approved },
    ];
  if (r.self_billed && r.objected_at) steps.push({ label: t("Dein Widerspruch", "Your objection"), value: fmt(r.objected_at), done: true });
  const partial = partialPaidOutCents(r);
  const brandPartly = !brandPaid && (brandState === "partial" || (partial > 0 && !brandState));
  if (!direct) steps.push({ label: t("Zahlung der Marke", "Payment by the brand"), value: brandPaid ? t("eingegangen", "received") : brandPartly ? t("teilweise eingegangen", "partly received") : open, done: brandPaid });
  let payout = open;
  const m = (c: number) => formatMoney(c, (isCurrency(r.currency) ? r.currency : "EUR") as Currency, lang === "en" ? "en-GB" : "de-DE");
  if (paidOut) payout = fmt(r.paid_at);
  // GELD-R3-3: anteilig ausgezahlt, der Rest folgt (nicht „ausstehend“ über den vollen Betrag)
  else if (partial > 0) payout = t(`anteilig ausgezahlt ${m(partial)} · Rest ${m(r.gross_cents - partial)} folgt`, `partly paid out ${m(partial)} · rest ${m(r.gross_cents - partial)} to follow`);
  else if (closed) payout = t("entfällt", "not applicable");
  else if (s === "payment_reported") payout = t(`von dir gemeldet am ${fmt(r.talent_payment_response_at)}`, `reported by you on ${fmt(r.talent_payment_response_at)}`);
  else if (v.state.confirmationActive) payout = t(`von deiner Agentur bestätigt am ${fmt(r.admin_payment_confirmed_at)} · bitte Eingang melden`, `confirmed by your agency on ${fmt(r.admin_payment_confirmed_at)} · please report receipt`);
  else if (s === "send_overdue" || s === "payout_overdue") {
    // Überfällig, auch wenn die Rechnung schon im Zahlungslauf steht (SICHT-CREATOR-14): nie „vorgemerkt“ neben „überfällig“
    const due = typeof v.state.labelParams?.dueDate === "string" ? v.state.labelParams.dueDate
      : v.record.payoutDueDate ?? r.effective_due_date ?? r.document_due_date ?? r.issuer_due_date;
    payout = t(`überfällig seit ${fmt(due)}`, `overdue since ${fmt(due)}`);
  }
  else if (inRun) payout = r.effective_due_date ? t(`vorgemerkt · fällig ${fmt(r.effective_due_date)}`, `scheduled · due ${fmt(r.effective_due_date)}`) : t("vorgemerkt", "scheduled");
  steps.push({ label: direct ? t("Zahlung der Marke an dich", "Payment from the brand to you") : t("Auszahlung an dich", "Payout to you"), value: payout, done: paidOut });
  return steps;
}

/** Offene Forderungen aus Creator-Sicht (RE-39, V228k #33): ohne Entwurf, Korrektur, Erledigtes und ohne selbst gemeldete Zahlung. */
export function openClaims(views: readonly TalentInvoiceView[]): { byCurrency: Record<string, number>; count: number; overdue: number; taxCents: number } {
  const excluded = new Set(["draft", "correction_needed", "cancelled", "cancelled_refund_pending", "credit_note", "paid", "bad_debt", "discarded", "withdrawn", "payment_reported", "payout_confirmed"]);
  const byCurrency: Record<string, number> = {};
  let count = 0;
  let overdue = 0;
  // Steuer in den offenen Rechnungen: der Hinweis „mit Umsatzsteuer“ nur, wenn sie wirklich welche enthalten (SICHT-CREATOR-04)
  let taxCents = 0;
  for (const v of views) {
    // CO-016: Stornobeleg nicht als offene Forderung (die stornierte Rechnung fällt schon heraus)
    if (excluded.has(v.state.state) || v.row.credit_for) continue;
    // Anteilig ausgezahlt: offen ist nur der Rest (GELD-R3-3)
    byCurrency[v.row.currency] = (byCurrency[v.row.currency] ?? 0) + (v.row.gross_cents ?? 0) - partialPaidOutCents(v.row);
    taxCents += Math.abs(v.row.tax_cents ?? 0);
    count += 1;
    // Überfällige Auszahlung der Agentur zählt mit (Befund 11)
    if (v.state.isOverdue || ["overdue", "money_missing", "send_overdue", "payout_overdue", "dunning"].includes(v.state.state)) overdue += 1;
  }
  return { byCurrency, count, overdue, taxCents };
}

/**
 * Steuerhinweis der Kachel „Noch auszuzahlen“ (SICHT-CREATOR-04): brutto heißt mit Umsatzsteuer bzw. MWST nur, wenn die
 * offenen Rechnungen Steuer enthalten; sonst ohne, bei Kleinunternehmern mit dem Grund.
 */
export function claimsTaxNote(claims: { taxCents: number; count: number }, talent: { small_business?: boolean | null; country?: string | null }, lang: Lang = "de"): string {
  const word = vatWord(talent.country, lang);
  // Ohne offene Rechnung zählt der Steuerstatus (CREATOR-R3-8): Kleinunternehmer bzw. nicht MWST-pflichtig rechnen ohne Steuer ab
  if (claims.taxCents > 0 || (claims.count === 0 && !talent.small_business)) return tr(lang, `brutto, also so, wie es auf deinen Rechnungen steht (mit ${word})`, "gross, as stated on your invoices (including VAT)");
  return talent.small_business
    ? tr(lang, `so, wie es auf deinen Rechnungen steht (ohne ${word}, ${creatorTaxTexts(talent.country, lang).smallShort})`, `as stated on your invoices (no VAT, ${creatorTaxTexts(talent.country, lang).smallShort})`)
    : tr(lang, `so, wie es auf deinen Rechnungen steht (ohne ${word})`, "as stated on your invoices (no VAT)");
}

// ── Anteil nach Provision je Monat ──────────────────────────────────────────

export const NO_REVENUE_MODELS = new Set(["barter", "event", "gifting"]);
/** Rechnungen, die für einen Monat nicht (mehr) zählen (wie die Übersicht der Agentur). */
export const INACTIVE_INVOICE = new Set(["draft", "cancelled", "withdrawn", "discarded", "rejected"]);

export interface CommissionInfo {
  /** Agenturanteil in Prozent */
  percent: number;
  source: "deal" | "talent" | "agency";
}

/**
 * Provisionssatz: Kooperation → eigenes Profil → Standard der Agentur. Den Agentur-Standard liefert talent_deal_rates
 * (0031) je eigener Kooperation, ohne dass der Creator die Agentur-Einstellungen liest (Befund 2).
 */
export function commissionOf(deal: Pick<CDeal, "commission_bp" | "post_term" | "post_term_from" | "post_term_until">, talent: Pick<TalentSelf, "commission_bp">, rate?: Pick<DealRate, "commission_bp" | "source"> | null, month?: string | null): CommissionInfo | null {
  try {
    // WS-19 in der Fachlogik: Deal → Talent → Agentur; im Nachlauf nach Austritt Satz je Abrechnungsmonat (0074)
    const dealBp = dealBpForMonth(deal, month);
    const r = resolveCommissionRate({
      deal: dealBp == null ? null : dealBp / 100,
      talent: talent.commission_bp == null ? null : talent.commission_bp / 100,
      agency: rate?.commission_bp == null ? Number.NaN : rate.commission_bp / 100,
    });
    return Number.isFinite(r.percent) ? { percent: r.percent, source: r.source } : null;
  } catch {
    return null;
  }
}

/** Kurzform: Satz einer eigenen Kooperation mit dem Agentur-Standard aus der Basis. */
export function rateOf(b: Pick<CreatorBase, "talent"> & { rates?: ReadonlyMap<string, DealRate> }, deal: CDeal, month?: string | null): CommissionInfo | null {
  return commissionOf(deal, b.talent, b.rates?.get(deal.id) ?? null, month);
}

/** Creator-Anteil netto aus Budget und Zusatzpositionen (ST-18: Einzelpreise nach Provision auf Cent gerundet). */
export function shareFromBudget(budgetCents: number, extras: readonly Pick<CLineItem, "description" | "amount_cents" | "commissionable">[], percent: number): number {
  const lines: LineInput[] = [
    { description: "Budget", quantity: 1, unitPriceCents: budgetCents },
    ...extras.map((e) => ({ description: e.description, quantity: 1, unitPriceCents: e.amount_cents, commissionFree: !e.commissionable })),
  ];
  // Die Agentur rechnet mit der Marke ab, der Creator mit der Agentur: Auszahlung = Creator-Anteil (computeDealSplit)
  return computeDealSplit({ lines, commissionPercent: percent, billing: "agency_invoices_brand" }).talentPayoutCents ?? 0;
}

/** Satz und Staffel einer eigenen Kooperation im Abrechnungsmonat; im Nachlauf nach Austritt gilt der Satz der Regel ohne Staffel (0074). */
export function rateWithTiers(deal: Pick<CDeal, "commission_bp" | "commission_tiers" | "post_term" | "post_term_from" | "post_term_until">, rate: Pick<CommissionInfo, "percent">,
  month?: string | null): { percent: number; tiers: CommissionTier[] | null } {
  const nachlauf = deal.post_term && month ? dealBpForMonth({ ...deal, commission_bp: null }, month) : null;
  return { percent: rate.percent, tiers: nachlauf != null ? null : tiersFromJson(deal.commission_tiers) };
}

/** Honorar (Monatsbetrag + provisionspflichtige Zusätze) und durchlaufende Posten eines Monats. */
function honorarOf(budgetCents: number, extras: readonly Pick<CLineItem, "amount_cents" | "commissionable">[]): { honorar: number; passThrough: number } {
  return {
    honorar: budgetCents + extras.filter((e) => e.commissionable).reduce((a, e) => a + e.amount_cents, 0),
    passThrough: extras.filter((e) => !e.commissionable).reduce((a, e) => a + e.amount_cents, 0),
  };
}

/**
 * Dein Anteil netto an einem Monat: Honorar nach Satz oder Staffel und nach der Agenturvergütung der Marke (shareOfHonorar),
 * plus durchlaufende Posten. Dieselbe Regel wie Aufteilung, Wächter und Gutschrift der Agentur (GELD-3, GELD-4).
 */
export function creatorShareCents(deal: Pick<CDeal, "commission_bp" | "commission_tiers" | "post_term" | "post_term_from" | "post_term_until" | "brand_discount_bp" | "brand_discount_bearer">,
  budgetCents: number, extras: readonly Pick<CLineItem, "amount_cents" | "commissionable">[], rate: Pick<CommissionInfo, "percent">, month?: string | null): number {
  const { honorar, passThrough } = honorarOf(budgetCents, extras);
  return shareOfHonorar(honorar, rateWithTiers(deal, rate, month), brandDiscountFromRow(deal)).creatorCents + passThrough;
}

/**
 * Netto-Betrag, den deine Rechnung für einen Monat nennen soll: über die Agentur dein Anteil (creatorShareCents), direkt an die
 * Marke das, was die Marke zahlt (Honorar nach ihrem Abzug plus durchlaufende Posten). Grundlage der Summenprobe vor dem Einreichen.
 */
export function expectedInvoiceNetCents(deal: CDeal, budgetCents: number, extras: readonly Pick<CLineItem, "amount_cents" | "commissionable">[], rate: Pick<CommissionInfo, "percent"> | null,
  month?: string | null): number | null {
  const { honorar, passThrough } = honorarOf(budgetCents, extras);
  if (isDirect(deal)) return honorar - shareOfHonorar(honorar, { percent: 0 }, brandDiscountFromRow(deal)).discountCents + passThrough;
  return rate ? creatorShareCents(deal, budgetCents, extras, rate, month) : null;
}

/** Agenturanteil eines Monats nach Satz oder Staffel und Abzug der Marke (für die Anzeige „minus Agenturanteil“). */
export function agencyShareOf(deal: CDeal, budgetCents: number, extras: readonly Pick<CLineItem, "amount_cents" | "commissionable">[], rate: Pick<CommissionInfo, "percent">,
  month?: string | null): { agencyCents: number; discountCents: number; percent: number; discount: BrandDiscount | null; tiers: boolean } {
  const { honorar } = honorarOf(budgetCents, extras);
  const r = rateWithTiers(deal, rate, month);
  const discount = brandDiscountFromRow(deal);
  const s = shareOfHonorar(honorar, r, discount);
  const base = discount?.bearer === "agency" ? honorar : honorar - s.discountCents;
  return { agencyCents: s.agencyCents, discountCents: s.discountCents, percent: r.tiers ? effectiveCommissionPercent(base, r) : r.percent, discount, tiers: !!r.tiers };
}

/** Zusatzpositionen eines Monats: mit Monat genau dieser, ohne Monat der erste Monat der Kooperation (extraInPeriod). */
export function extrasOfPeriod(lineItems: readonly CLineItem[], periods: readonly Pick<CPeriod, "id" | "month" | "deal_id">[], period: Pick<CPeriod, "id" | "deal_id">): CLineItem[] {
  const own = periods.filter((p) => p.deal_id === period.deal_id);
  return lineItems.filter((e) => e.deal_id === period.deal_id && extraInPeriod(e.period_id, period.id, own));
}

export type ShareCategory = "paid" | "open" | "pipeline";

export interface Occurrence {
  /** invoice = eigene Rechnung ohne Kooperationsmonat (Nachberechnung), sie hat keinen Monat, der sie sonst mitzählt */
  source: "deal" | "booking" | "invoice";
  key: string;
  dealId: string | null;
  periodId: string | null;
  brandId: string | null;
  month: MonthKey;
  currency: string;
  /** Monatsbetrag (Honorar netto, Creator + Agentur) */
  budgetCents: number;
  extraCents: number;
  /** Dein Anteil netto; null = Satz unbekannt und noch keine Rechnung */
  shareCents: number | null;
  actual: boolean;
  category: ShareCategory;
  /**
   * Geliefert bzw. von der Agentur abgerechnet, aber ohne eigene Rechnung (Befund 1): ohne Rechnung zahlt die Agentur
   * nie aus. Zählt als „offen“, wird aber getrennt ausgewiesen („Rechnung fehlt noch“).
   */
  missingInvoice?: boolean;
}

export interface OccurrenceInput {
  today: IsoDate;
  talent: Pick<TalentSelf, "commission_bp">;
  deals: readonly CDeal[];
  periods: readonly CPeriod[];
  lineItems: readonly CLineItem[];
  invoices: readonly CInvoice[];
  bookings: readonly CBooking[];
  /** Wirksame Sätze je Kooperation (talent_deal_rates) */
  rates?: ReadonlyMap<string, DealRate>;
  /** Provisionsrechnungen der Agentur an den Creator (direkte Abrechnung) */
  agencyInvoices?: readonly CInvoice[];
}

/** Arbeitsstände, ab denen ein Monat als geliefert gilt und abgerechnet wird. */
const DELIVERED_STATES = new Set(["invoicing", "done"]);
/** Veröffentlicht oder im Reporting: in einem vergangenen Monat ist die Leistung erbracht (Befund 1). */
const PUBLISHED_STATES = new Set(["published", "reporting"]);

/**
 * Monat aus Creator-Sicht geliefert bzw. abgerechnet: angefordert, von der Agentur abgerechnet, Arbeitsstand „Abrechnung“
 * oder „Abgeschlossen“, oder ein vergangener Monat mit veröffentlichtem Content. Künftige Monate nie.
 */
export function monthDelivered(p: Pick<CPeriod, "month" | "manual_status" | "invoice_requested_at" | "status">, d: Pick<CDeal, "status">, today: IsoDate): boolean {
  const month = p.month.slice(0, 7);
  const current = today.slice(0, 7);
  if (month > current) return false;
  const work = p.manual_status ?? d.status;
  return !!p.invoice_requested_at || ["invoice_requested", "invoiced", "paid"].includes(p.status) || DELIVERED_STATES.has(work)
    || (month < current && PUBLISHED_STATES.has(work));
}

/** Hat der Monat ein Honorar, für das der Creator eine Rechnung stellt? (nicht Barter ohne Budget, Event, Gifting, reine Provision) */
export function monthHasFee(p: Pick<CPeriod, "amount_cents">, d: Pick<CDeal, "model" | "budget_cents">): boolean {
  if (d.model === "commission_only") return false;
  if (NO_REVENUE_MODELS.has(d.model) && !(d.model === "barter" && (d.budget_cents ?? 0) > 0)) return false;
  return (p.amount_cents ?? 0) > 0;
}

/**
 * Befund 1: Monat geliefert bzw. abgerechnet, mit Honorar, nicht gebucht, ohne eigene gültige Rechnung (Entwürfe zählen
 * nicht). Ohne Rechnung zahlt die Agentur nie aus; der Monat ist deshalb nicht erledigt.
 */
export function monthNeedsInvoice(p: CPeriod, d: CDeal, invoices: readonly CInvoice[], today: IsoDate): boolean {
  if (d.status === "cancelled" || d.status === "blocked" || p.status === "cancelled" || p.booked_at) return false;
  // Im bisherigen System der Agentur schon abgerechnet (0064): die Rechnung liegt dort, hier fehlt nichts
  if (p.imported_state) return false;
  if (!monthHasFee(p, d) || !monthDelivered(p, d, today)) return false;
  return liveTalentInvoicesFor(p.id, invoices).length === 0;
}

export function liveTalentInvoicesFor(periodId: string, invoices: readonly CInvoice[]): CInvoice[] {
  // CO-016: ein Stornobeleg (credit_for) hebt seine Rechnung auf, die als „storniert“ schon herausfällt; er zählt nicht noch einmal
  return invoices.filter((i) => i.period_id === periodId && i.kind !== "agency_to_brand" && !INACTIVE_INVOICE.has(i.status) && !i.credit_for);
}

/**
 * Monate der eigenen Kooperationen und sichtbare Buchungen als Umsatz-Vorkommen aus Creator-Sicht.
 * Kategorie: ausgezahlt (eigene Rechnung bezahlt oder Monat gebucht), offen (geliefert bzw. abgerechnet,
 * Auszahlung steht aus) oder voraussichtlich (geplant, künftige Monate). Barter, Event und Gifting zählen nie,
 * stornierte Kooperationen nie, blockierte nur mit gebuchtem Monat (wie die Übersicht der Agentur).
 */
export function creatorOccurrences(input: OccurrenceInput): Occurrence[] {
  const current = input.today.slice(0, 7);
  const deals = new Map(input.deals.map((d) => [d.id, d]));
  const byPeriod = new Map<string, CLineItem[]>();
  for (const e of input.lineItems) {
    const k = e.period_id ?? `deal:${e.deal_id}`;
    byPeriod.set(k, [...(byPeriod.get(k) ?? []), e]);
  }
  const firstPeriod = new Map<string, string>();
  for (const p of [...input.periods].sort((a, b) => a.month.localeCompare(b.month))) if (!firstPeriod.has(p.deal_id)) firstPeriod.set(p.deal_id, p.id);

  const out: Occurrence[] = [];
  for (const p of input.periods) {
    const d = deals.get(p.deal_id);
    if (!d || NO_REVENUE_MODELS.has(d.model) || d.status === "cancelled") continue;
    const own = liveTalentInvoicesFor(p.id, input.invoices);
    // Ausgezahlt: bezahlt, gebucht oder von der Agentur als ausgezahlt vermerkt (Eingang noch zu bestätigen, GELD-15)
    const paidOut = own.some((i) => i.status === "paid" || (i.kind === "talent_to_agency" && !!i.admin_payment_confirmed_at)) || !!p.booked_at;
    const booked = !!p.booked_at || p.status === "paid";
    if (d.status === "blocked" && !booked) continue;
    const commissionOnly = d.model === "commission_only";
    // Nachtest CO-015: eine zur Korrektur zurückgegebene Rechnung bestimmt den Anteil nicht, es gilt der vereinbarte
    // (bei „nur Provision“ gibt es keinen vereinbarten Betrag, dort bleibt die Rechnung die Grundlage)
    const zaehlend = commissionOnly ? own : own.filter((i) => i.status !== "revision_requested");
    const ownNet = zaehlend.length ? zaehlend.reduce((a, i) => a + (i.net_cents ?? 0), 0) : null;
    if (commissionOnly && ownNet === null) continue; // Umsatz läuft direkt über die Marke, im Portal nicht erfasst
    const budget = commissionOnly ? 0 : (p.amount_cents ?? 0);
    const extras = [...(byPeriod.get(p.id) ?? []), ...(firstPeriod.get(d.id) === p.id ? byPeriod.get(`deal:${d.id}`) ?? [] : [])];
    const rate = commissionOf(d, input.talent, input.rates?.get(d.id) ?? null, p.month);
    // Direkt an die Marke: die eigene Rechnung ist der volle Betrag; dein Anteil ist er abzüglich der Provisionsrechnung
    // der Agentur (liegt sie noch nicht vor, nach Satz)
    let actualNet = ownNet;
    if (ownNet !== null && isDirect(d)) {
      const fee = (input.agencyInvoices ?? []).filter((i) => i.period_id === p.id && !INACTIVE_INVOICE.has(i.status));
      // Ohne Provisionsrechnung: die vereinbarte Provision (Satz oder Staffel, nach dem Abzug der Marke)
      actualNet = fee.length ? ownNet - fee.reduce((a, i) => a + (i.net_cents ?? 0), 0)
        : rate ? ownNet - (commissionOnly ? commissionCents(ownNet, rateWithTiers(d, rate, p.month)) : agencyShareOf(d, budget, extras, rate, p.month).agencyCents) : null;
    }
    // Beträge schon als Anteil (0079): nichts mehr abziehen; sonst Anteil mit Staffel und Abzug der Marke (0075)
    const share = actualNet ?? (amountsAreShare(d) ? shareFromBudget(budget, extras, 0) : rate ? creatorShareCents(d, budget, extras, rate, p.month) : null);
    const month = p.month.slice(0, 7);
    let category: ShareCategory;
    if (paidOut) category = "paid";
    else if (month > current) category = "pipeline";
    else if (own.length || monthDelivered(p, d, input.today)) category = "open";
    else category = "pipeline";
    out.push({
      source: "deal", key: p.id, dealId: d.id, periodId: p.id, brandId: d.brand_id, month, currency: d.currency,
      budgetCents: budget, extraCents: extras.reduce((a, e) => a + e.amount_cents, 0), shareCents: share, actual: actualNet !== null, category,
      // Im bisherigen System schon abgerechnet (0064): keine fehlende Rechnung, gleiche Regel wie monthNeedsInvoice (PRAXIS-R2-2)
      missingInvoice: category === "open" && own.length === 0 && !p.imported_state,
    });
  }
  // Eigene Rechnungen ohne Kooperationsmonat (Nachberechnung im Assistenten): Sie zählten bisher nirgends mit, „Dein Geld“
  // blieb trotz Auszahlung auf 0 (Praxistest K-058). Monat = Leistungszeitraum, sonst Rechnungsdatum.
  for (const i of input.invoices) {
    if (i.period_id || i.kind === "agency_to_brand" || i.kind === "agency_to_talent" || INACTIVE_INVOICE.has(i.status) || i.status === "draft" || i.credit_for) continue;
    const tag = i.service_period_start ?? i.issue_date;
    if (!tag || i.net_cents == null) continue;
    const d = i.deal_id ? deals.get(i.deal_id) : undefined;
    out.push({
      source: "invoice", key: `invoice:${i.id}`, dealId: i.deal_id, periodId: null, brandId: i.brand_id ?? d?.brand_id ?? null, month: tag.slice(0, 7),
      currency: i.currency, budgetCents: 0, extraCents: 0, shareCents: i.net_cents, actual: true,
      category: i.status === "paid" || (i.kind === "talent_to_agency" && !!i.admin_payment_confirmed_at) ? "paid" : "open",
    });
  }
  for (const b of input.bookings) {
    if (b.status === "cancelled" || b.kind === "agency_fee") continue;
    out.push({
      source: "booking", key: `booking:${b.id}`, dealId: b.deal_id, periodId: b.period_id, brandId: b.brand_id, month: b.booked_on.slice(0, 7),
      currency: b.currency, budgetCents: b.amount_cents ?? b.talent_share_cents ?? 0, extraCents: 0, shareCents: b.talent_share_cents, actual: b.talent_share_cents != null,
      category: b.status === "booked" ? "paid" : "pipeline",
    });
  }
  return out;
}

// ── Übersicht ───────────────────────────────────────────────────────────────

export type RangeKey = "6m" | "ytd" | "12m" | "24m" | "prev" | "all" | "custom";

export interface Range {
  from: MonthKey;
  to: MonthKey;
  label: string;
  short: string;
}

export function rangeLabel(from: MonthKey, to: MonthKey, lang: Lang = "de"): { label: string; short: string } {
  const mName = (m: MonthKey) => monthOf(m, lang, false);
  const mShort = (m: MonthKey) => monthShortOf(m, lang);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  if (from === to) return { label: `${mName(from)} ${from.slice(0, 4)}`, short: mShort(from) };
  return {
    label: sameYear ? `${mName(from)}–${mName(to)} ${to.slice(0, 4)}` : `${mName(from)} ${from.slice(0, 4)} – ${mName(to)} ${to.slice(0, 4)}`,
    short: sameYear ? `${mShort(from)}–${mShort(to)}` : `${mShort(from)} ${from.slice(2, 4)}–${mShort(to)} ${to.slice(2, 4)}`,
  };
}

export function rangeOf(key: RangeKey, today: IsoDate, opts: { earliest?: MonthKey | null; latest?: MonthKey | null; from?: MonthKey; to?: MonthKey } = {}, lang: Lang = "de"): Range {
  const cur = today.slice(0, 7);
  const year = today.slice(0, 4);
  let from: MonthKey;
  let to: MonthKey = cur;
  switch (key) {
    case "ytd": from = `${year}-01`; break;
    case "12m": from = addMonths(cur, -11); break;
    case "24m": from = addMonths(cur, -23); break;
    case "prev": {
      const y = String(Number(year) - 1);
      from = `${y}-01`;
      to = `${y}-12`;
      break;
    }
    case "all":
      from = opts.earliest && opts.earliest < cur ? opts.earliest : cur;
      to = opts.latest && opts.latest > cur ? opts.latest : cur;
      break;
    case "custom":
      from = opts.from ?? addMonths(cur, -5);
      to = opts.to ?? cur;
      if (from > to) [from, to] = [to, from];
      break;
    default: from = addMonths(cur, -5);
  }
  const l = key === "all" ? { label: tr(lang, "Gesamt", "All time"), short: tr(lang, "Gesamt", "All") } : rangeLabel(from, to, lang);
  return { from, to, ...l };
}

/** Umrechnung mit dem letzten Kurs bis Monatsende, direkt, umgekehrt oder über den Euro (wie app.fx_rate); null ohne Kurs. */
export function fxConverter(rates: readonly { base: string; quote: string; valid_on: string; rate: number }[]) {
  return fxMonthConverter(rates) as (cents: number, from: string, to: string, month: MonthKey) => number | null;
}

export interface CreatorOverview {
  current: MonthKey;
  year: string;
  monthEarnedCents: number;
  yearEarnedCents: number;
  forecastCents: number;
  forecastCount: number;
  /**
   * Kette im Zeitraum, alles netto (Befund 16): verdient = ausgezahlt + Rechnung liegt vor + Rechnung fehlt noch.
   * openCents ist die Summe der beiden offenen Teile.
   */
  period: { earnedCents: number; paidCents: number; openCents: number; openInvoicedCents: number; openMissingCents: number; missingCount: number; avgDealCents: number; dealCount: number };
  activeDeals: number;
  topBrand: { brandId: string; cents: number } | null;
  months: { month: MonthKey; earnedCents: number; pipelineCents: number }[];
  /** Top-Marken nach verdientem Anteil (ohne Voraussichtliches) */
  topBrands: { brandId: string; cents: number }[];
  status: { paidCents: number; openCents: number; invoicedCents: number; missingCents: number; pipelineCents: number };
  /** Aktive Kooperationen je Stand: Anzahl, Honorar (Creator und Agentur) und dein noch nicht ausgezahlter Anteil */
  pipeline: { status: string; deals: number; budgetCents: number; shareCents: number }[];
  /** Monate ohne bekannten Anteil (kein eigener Satz, noch keine Rechnung): voraussichtliche (alle) und verdiente im Zeitraum */
  unknownPipeline: number;
  unknownInRange: number;
  /** Vorkommen in anderer Währung ohne Kurs */
  unconverted: number;
}

export function computeCreatorOverview(input: {
  today: IsoDate;
  range: Range;
  currency: string;
  occurrences: readonly Occurrence[];
  deals: readonly CDeal[];
  periods: readonly CPeriod[];
  convert?: (cents: number, from: string, to: string, month: MonthKey) => number | null;
  /** Monate, die das Diagramm über das Zeitraumende hinaus zeigt (Vorschau auf Geplantes) */
  chartAhead?: number;
  /** Eigene Rechnungen: mit ihnen gilt „aktiv“ wie in „Deine Deals“ (ein Monat ist noch nicht erledigt, Befund 16) */
  invoices?: readonly CInvoice[];
}): CreatorOverview {
  const { today, range, currency } = input;
  const current = today.slice(0, 7);
  const year = today.slice(0, 4);
  const conv = (c: number, from: string, month: MonthKey) => (from === currency ? c : input.convert ? input.convert(c, from, currency, month) : null);
  let unconverted = 0;
  const occ = input.occurrences.flatMap((o) => {
    const share = o.shareCents === null ? null : conv(o.shareCents, o.currency, o.month);
    const budget = conv(o.budgetCents + o.extraCents, o.currency, o.month);
    if ((o.shareCents !== null && share === null) || budget === null) {
      unconverted += 1;
      return [];
    }
    return [{ ...o, share, total: budget }];
  });
  const known = occ.filter((o) => o.share !== null) as (typeof occ[number] & { share: number })[];
  const inRange = (m: MonthKey) => m >= range.from && m <= range.to;
  const sum = (list: readonly { share: number }[]) => list.reduce((a, o) => a + o.share, 0);
  const earned = (o: { category: ShareCategory }) => o.category === "paid" || o.category === "open";

  const rangeKnown = known.filter((o) => inRange(o.month));
  const rangeAll = occ.filter((o) => inRange(o.month));
  const dealsInRange = new Set(rangeAll.filter((o) => o.source === "deal" && o.dealId).map((o) => o.dealId as string));
  const budgetInRange = rangeAll.filter((o) => o.source === "deal").reduce((a, o) => a + o.total, 0);

  const brandMap = new Map<string, number>();
  // Top-Marken nur nach Verdientem; Geplantes steht getrennt (Befund 16)
  for (const o of rangeKnown) if (o.brandId && earned(o)) brandMap.set(o.brandId, (brandMap.get(o.brandId) ?? 0) + o.share);
  const topBrands = [...brandMap.entries()].filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]).map(([brandId, cents]) => ({ brandId, cents }));

  const chartTo = range.to >= current && (input.chartAhead ?? 0) > 0 ? addMonths(range.to, input.chartAhead ?? 0) : range.to;
  const months = monthRange(range.from, chartTo).map((m) => {
    const list = known.filter((o) => o.month === m);
    return { month: m, earnedCents: sum(list.filter(earned)), pipelineCents: sum(list.filter((o) => o.category === "pipeline")) };
  });

  const pipelineAll = known.filter((o) => o.category === "pipeline");
  const periodsByDeal = new Map<string, CPeriod[]>();
  for (const p of input.periods) periodsByDeal.set(p.deal_id, [...(periodsByDeal.get(p.deal_id) ?? []), p]);
  // „Aktiv“ überall gleich (Befund 16): mit Rechnungen wie in „Deine Deals“ (ein Monat ist nicht erledigt), sonst wie bisher
  const activeIds = input.invoices ? activeDealIds(input.deals, input.periods, input.invoices, today) : null;
  const openByDeal = new Set(occ.filter((o) => o.category !== "paid" && o.dealId && (o.month >= range.from || o.category === "open")).map((o) => o.dealId as string));
  const activeDeals = activeIds ? input.deals.filter((d) => activeIds.has(d.id)) : input.deals.filter((d) => !d.archived_at && !["done", "cancelled"].includes(d.status) && !NO_REVENUE_MODELS.has(d.model)
    && (openByDeal.has(d.id) || !(periodsByDeal.get(d.id)?.length)));

  const unpaidShare = new Map<string, number>();
  for (const o of known) if (o.dealId && o.category !== "paid") unpaidShare.set(o.dealId, (unpaidShare.get(o.dealId) ?? 0) + o.share);
  const pipelineMap = new Map<string, { deals: number; budgetCents: number; shareCents: number }>();
  for (const d of activeIds ? activeDeals : input.deals) {
    if (!activeIds && (d.archived_at || ["done", "cancelled"].includes(d.status))) continue;
    const e = pipelineMap.get(d.status) ?? { deals: 0, budgetCents: 0, shareCents: 0 };
    e.deals += 1;
    e.budgetCents += (periodsByDeal.get(d.id) ?? []).reduce((a, p) => a + (conv(p.amount_cents ?? 0, d.currency, p.month.slice(0, 7)) ?? 0), 0);
    e.shareCents += unpaidShare.get(d.id) ?? 0;
    pipelineMap.set(d.status, e);
  }

  const unknownPipeline = occ.filter((o) => o.share === null && o.category === "pipeline").length;
  const unknownInRange = occ.filter((o) => o.share === null && o.category !== "pipeline" && inRange(o.month)).length;
  return {
    current,
    year,
    monthEarnedCents: sum(known.filter((o) => o.month === current && earned(o))),
    yearEarnedCents: sum(known.filter((o) => o.month.startsWith(year) && earned(o))),
    forecastCents: sum(pipelineAll),
    forecastCount: pipelineAll.length,
    period: {
      earnedCents: sum(rangeKnown.filter(earned)),
      paidCents: sum(rangeKnown.filter((o) => o.category === "paid")),
      openCents: sum(rangeKnown.filter((o) => o.category === "open")),
      openInvoicedCents: sum(rangeKnown.filter((o) => o.category === "open" && !o.missingInvoice)),
      openMissingCents: sum(rangeKnown.filter((o) => o.category === "open" && !!o.missingInvoice)),
      missingCount: rangeAll.filter((o) => o.category === "open" && !!o.missingInvoice).length,
      avgDealCents: dealsInRange.size ? Math.round(budgetInRange / dealsInRange.size) : 0,
      dealCount: dealsInRange.size,
    },
    activeDeals: activeDeals.length,
    topBrand: topBrands[0] ?? null,
    months,
    topBrands,
    status: {
      paidCents: sum(rangeKnown.filter((o) => o.category === "paid")),
      openCents: sum(rangeKnown.filter((o) => o.category === "open")),
      invoicedCents: sum(rangeKnown.filter((o) => o.category === "open" && !o.missingInvoice)),
      missingCents: sum(rangeKnown.filter((o) => o.category === "open" && !!o.missingInvoice)),
      pipelineCents: sum(rangeKnown.filter((o) => o.category === "pipeline")),
    },
    pipeline: [...pipelineMap.entries()].map(([status, e]) => ({ status, ...e })).sort((a, b) => DEAL_STATUS_ORDER.indexOf(a.status) - DEAL_STATUS_ORDER.indexOf(b.status)),
    unknownPipeline,
    unknownInRange,
    unconverted,
  };
}

// ── Kooperationen und Monate ────────────────────────────────────────────────

export const DEAL_STATUS_ORDER = ["planning", "contract", "concept", "production", "content_approval", "published", "reporting", "invoicing", "done", "blocked", "cancelled"];
// Stand „Vertrag“ heißt für den Creator: der Vertrag ist in Unterschrift (nicht unbedingt noch bei ihm, Befund 22)
const DEAL_STATUS: Record<string, [string, string, Tone]> = {
  planning: ["In Planung", "Planning", "neutral"],
  contract: ["Vertrag in Unterschrift", "Contract being signed", "info"],
  concept: ["Konzept", "Concept", "info"],
  production: ["In Produktion", "In production", "info"],
  content_approval: ["Freigabe Content", "Content approval", "warning"],
  published: ["Veröffentlicht", "Published", "success"],
  reporting: ["Reporting", "Reporting", "info"],
  invoicing: ["Abrechnung", "Invoicing", "warning"],
  done: ["Abgeschlossen", "Completed", "success"],
  blocked: ["Blockiert", "Blocked", "error"],
  cancelled: ["Storniert", "Cancelled", "neutral"],
};
/** Eigene Bezeichnungen der Agentur je Status (org_status_labels, SAAS-10); leer = Standard des Portals */
export type StatusLabels = ReadonlyMap<string, { de: string | null; en: string | null }>;

export function dealStatusPill(status: string | null | undefined, lang: Lang = "de", labels?: StatusLabels): Pill {
  const s = DEAL_STATUS[status ?? ""];
  // SAAS-10: benennt die Agentur einen Status um, sieht der Creator denselben Begriff (Englisch nur mit eigener englischer Fassung)
  const own = status ? labels?.get(status) : undefined;
  const custom = (lang === "en" ? own?.en : own?.de)?.trim();
  if (s) return pill(custom || tr(lang, s[0], s[1]), s[2]);
  return pill(custom || status || tr(lang, "Offen", "Open"), "neutral");
}

/** Modellnamen aus dem gemeinsamen Katalog (deal-models.ts, 0047), damit Creator dieselben Begriffe sehen wie die Agentur. */
export const MODEL_LABEL: Record<string, string> = Object.fromEntries(DEAL_MODEL_KEYS.map((k) => [k, dealModelName(k, "de")]));
export function modelLabel(model: string, lang: Lang = "de"): string {
  return dealModelName(model, lang);
}
export const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  ugc: "UGC",
  podcast: "Podcast",
  other: "Weitere",
};
/** Bezeichnung einer Plattform: aus dem Katalog der Agentur (0047/0053), sonst Standardname, sonst der Schlüssel. */
export function platformLabel(p: string, lang: Lang = "de", names?: ReadonlyMap<string, { de: string; en: string | null }>): string {
  const own = names?.get(p);
  if (own) return lang === "en" ? own.en ?? own.de : own.de;
  return p === "other" ? tr(lang, "Weitere", "Other") : PLATFORM_LABEL[p] ?? p;
}

/**
 * Steuerhinweise für Kooperationen ohne Rechnung bzw. mit pauschal versteuerten Produkten (Befund 27). Kurz und ohne
 * Beratung: der Warenwert kann bei Regelbesteuerung eine Einnahme sein; § 37b EStG heißt, die Marke hat die Steuer übernommen.
 */
export function barterTaxNote(lang: Lang = "de"): string {
  // Steuerprüfung 23.10.2026, L10: neben der Einkommensteuer auch die Umsatzsteuer. Leistung gegen Produkte ist ein tauschähnlicher
  // Umsatz (§ 3 Abs. 12 Satz 2 UStG), Entgelt ist der Marktwert der Produkte (§ 10 Abs. 2 Satz 2 UStG); an eine Marke als
  // Unternehmerin besteht Rechnungspflicht (§ 14 Abs. 2 Satz 1 Nr. 2 UStG).
  return tr(lang,
    "Der Warenwert kann für dich steuerpflichtig sein (Tausch gegen deine Leistung). Bist du regelbesteuert, ist deine Leistung gegen Produkte auch umsatzsteuerpflichtig (Marktwert als Entgelt, § 10 Abs. 2 Satz 2 UStG); dann stellst du der Marke darüber eine Rechnung mit Umsatzsteuer.",
    "The value of the goods may be taxable income for you (exchanged for your services). If you are subject to standard VAT, your services in exchange for products are also subject to VAT (market value as consideration, § 10(2) sentence 2 German VAT Act); you then issue the brand an invoice with VAT for them.");
}
/**
 * § 37b EStG gilt nur im deutschen Steuerrecht (Befund Creator 22): mit Land der Agentur außerhalb Deutschlands ein
 * neutraler Hinweis. Ohne Angabe bleibt der bisherige Text (Aufrufer ohne Land).
 */
export function flatTaxNote(lang: Lang = "de", agencyCountry?: string | null): string {
  if (agencyCountry !== undefined && normalizeCountry(agencyCountry ?? null) !== "DE") {
    return tr(lang,
      "Die Marke hat angegeben, die Steuer auf die Produkte pauschal zu übernehmen. Ob du den Warenwert trotzdem angeben musst, klärt im Zweifel deine Steuerberatung.",
      "The brand has stated that it pays a flat-rate tax on the products. If you are unsure whether you still need to declare their value, ask your tax adviser.");
  }
  // Steuerprüfung 23.10.2026: § 37b Abs. 1 Satz 1 Nr. 1 EStG erfasst nur Zuwendungen „zusätzlich zur ohnehin vereinbarten Leistung
  // oder Gegenleistung“. Produkte, die selbst die vereinbarte Gegenleistung für die Inhalte sind (Tausch), kann die Marke nicht
  // pauschal versteuern; sie bleiben beim Creator Einnahme. Außer Ansatz (§ 37b Abs. 3 Satz 1) bleiben sie nur bei wirksamer
  // Pauschalierung, über die die Marke unterrichten muss (§ 37b Abs. 3 Satz 3). Vorher sagte der Satz ohne Bedingung „nicht angeben“.
  return tr(lang,
    "Die Marke gibt an, die Steuer auf die Produkte pauschal zu übernehmen (§ 37b EStG). Dann bleiben sie bei dir außer Ansatz. Das gilt nur für Produkte, die du zusätzlich zur vereinbarten Vergütung bekommst; sind sie selbst die Gegenleistung für deine Inhalte, sind sie Einnahme (Marktwert).",
    "The brand states that it pays a flat-rate tax on the products (Section 37b German Income Tax Act). They are then not part of your income. This only applies to products you receive in addition to the agreed fee; if they are themselves the consideration for your content, they are income (market value).");
}

/** Was ein Monat zur Anzeige zusätzlich braucht: heute, die Rechnungs-Sicht wie in „Rechnungen“, Gutschriftverfahren. */
export interface MonthContext {
  today: IsoDate;
  /** Rechnungen aus Talent-Sicht (viewTalentInvoice) je Rechnung: dieselbe Pille wie in der Rechnungsliste (Befund 12) */
  views?: ReadonlyMap<string, TalentInvoiceView>;
  /** Gutschriftverfahren vereinbart: die Agentur stellt die Rechnung im Namen des Creators aus */
  selfBilling?: boolean;
  /** Eigene Statusbezeichnungen der Agentur (SAAS-10) */
  statusLabels?: StatusLabels;
}

/** Rechnung eines Monats, die seinen Stand bestimmt: Korrektur zuerst, dann die jüngste gültige, Entwurf zuletzt. */
function leadingInvoice(mine: readonly CInvoice[]): CInvoice | null {
  const fix = mine.find((i) => i.status === "revision_requested" || i.status === "rejected");
  if (fix) return fix;
  const live = mine.filter((i) => i.status !== "draft").sort((a, b) => (b.submitted_at ?? b.created_at).localeCompare(a.submitted_at ?? a.created_at));
  return live.find((i) => i.status !== "paid") ?? live[0] ?? null;
}

/**
 * Stand eines Monats aus Creator-Sicht (KA-10): die eigene Rechnung schlägt den Arbeitsstand, mit derselben Pille wie in
 * „Rechnungen“ (Befund 12). Geliefert oder abgerechnet ohne Rechnung heißt „Rechnung fehlt“ (Befund 1); künftige Monate
 * ohne eigenen Stand sind „Geplant“ statt des Stands der ganzen Kooperation (Befund 23).
 */
export function monthPill(period: CPeriod, deal: CDeal, invoices: readonly CInvoice[], lang: Lang = "de", ctx?: MonthContext): Pill {
  const t = (de: string, en: string) => tr(lang, de, en);
  const mine = invoices.filter((i) => i.period_id === period.id && i.kind !== "agency_to_brand" && !["withdrawn", "discarded", "cancelled"].includes(i.status));
  const direct = isDirect(deal);
  const lead = leadingInvoice(mine);
  const view = lead ? ctx?.views?.get(lead.id) : undefined;
  if (view) return view.pill;
  if (lead) {
    if (lead.status === "revision_requested" || lead.status === "rejected") return pill(t("Korrektur nötig", "Correction needed"), "error");
    if (lead.status === "paid") return pill(direct ? t("Bezahlt", "Paid") : t("Ausgezahlt", "Paid out"), "success");
    if (lead.status === "sent") return pill(direct ? t("Bei der Marke", "With the brand") : t("Auszahlung läuft", "Payout in progress"), "info");
    if (lead.status === "approved") return pill(t("Freigegeben", "Approved"), "info");
    if (lead.status === "submitted") return pill(t("In Prüfung", "In review"), "warning");
  }
  if (period.booked_at) return pill(t("Gebucht", "Booked"), "success");
  const draft = mine.some((i) => i.status === "draft");
  if (ctx && monthNeedsInvoice(period, deal, invoices, ctx.today)) {
    if (ctx.selfBilling) return pill(t("Gutschrift folgt", "Self-billed invoice to follow"), "info");
    if (draft) return pill(t("Rechnung im Entwurf", "Invoice in draft"), "warning");
    return pill(period.invoice_requested_at ? t("Rechnung fehlt · angefordert", "Invoice missing · requested") : t("Rechnung fehlt", "Invoice missing"), "warning");
  }
  if (period.invoice_requested_at) return pill(draft ? t("Rechnung im Entwurf", "Invoice in draft") : t("Rechnung ausstehend", "Invoice outstanding"), "warning");
  // Ein Monat, der noch nicht begonnen hat, kann nicht veröffentlicht, im Reporting oder abgerechnet sein (Befund 23)
  const work = period.manual_status ?? deal.status;
  if (ctx && period.month.slice(0, 7) > ctx.today.slice(0, 7) && !["blocked", "cancelled"].includes(work)
    && (!period.manual_status || PUBLISHED_STATES.has(work) || DELIVERED_STATES.has(work))) {
    return pill(t("Geplant", "Planned"), "neutral");
  }
  return dealStatusPill(period.manual_status ?? deal.status, lang, ctx?.statusLabels);
}

/**
 * Monat gilt aus Creator-Sicht als erledigt: ausgezahlt oder gebucht ohne weitere offene Rechnung, oder abgeschlossen ohne
 * offene Rechnung. Geliefert, aber ohne eigene Rechnung, ist nie erledigt (Befund 1): ohne Rechnung keine Auszahlung.
 */
export function monthDone(period: CPeriod, deal: CDeal, invoices: readonly CInvoice[], today?: IsoDate): boolean {
  const mine = invoices.filter((i) => i.period_id === period.id && i.kind !== "agency_to_brand" && !["withdrawn", "discarded", "cancelled"].includes(i.status));
  if (mine.some((i) => i.status === "paid") || period.booked_at) return !mine.some((i) => i.status !== "paid" && i.status !== "draft");
  if (today && monthNeedsInvoice(period, deal, invoices, today)) return false;
  const state = period.manual_status ?? deal.status;
  return (state === "done" || deal.status === "done" || deal.status === "cancelled") && !mine.some((i) => i.status !== "paid") && !period.invoice_requested_at;
}

/**
 * Aktive Kooperationen, überall gleich gezählt (Befund 16): mindestens ein Monat ist noch nicht erledigt, oder die
 * Kooperation hat noch keine Monate und ist weder abgeschlossen noch storniert.
 */
export function activeDealIds(deals: readonly CDeal[], periods: readonly CPeriod[], invoices: readonly CInvoice[], today?: IsoDate): Set<string> {
  const byDeal = new Map<string, CPeriod[]>();
  for (const p of periods) byDeal.set(p.deal_id, [...(byDeal.get(p.deal_id) ?? []), p]);
  const out = new Set<string>();
  for (const d of deals) {
    if (d.archived_at) continue;
    const ps = byDeal.get(d.id) ?? [];
    if (ps.length ? ps.some((p) => !monthDone(p, d, invoices, today)) : !["done", "cancelled"].includes(d.status)) out.add(d.id);
  }
  return out;
}

/**
 * Content-Frist eines Monats; die Frist der Kooperation gilt nur, wenn sie genau einen Monat hat. CREATOR-R2-7: Tragen
 * Beiträge die Abgabe (im Monat oder ohne Monat, nicht entfallen), gilt die nächste Frist je Beitrag wie in Übersicht,
 * Kalender und Erinnerung; sind alle live, gibt es keine Frist mehr.
 */
export function monthDeadline(deal: CDeal, period: CPeriod | null, periodCount: number, deliverables: readonly CDeliverable[] = []): IsoDate | null {
  const own = deliverables.filter((x) => x.deal_id === deal.id && x.status !== "cancelled" && (!period || !x.period_id || x.period_id === period.id));
  if (own.length) return own.map((x) => nextDue(x)?.date ?? null).filter((d): d is IsoDate => !!d).sort()[0] ?? null;
  return period?.content_deadline ?? (periodCount <= 1 ? deal.content_deadline : null);
}

// ── Nächste Schritte ────────────────────────────────────────────────────────

export interface Step {
  key: string;
  to: string;
  title: string;
  detail: string;
  due: IsoDate | null;
  tone: Tone;
  /** info: kein To-do, nur „läuft gerade“ (z. B. Content in Freigabe, Befund 9) */
  kind?: "task" | "info";
}

/** „Heute“, „Morgen“, „Überfällig“ oder Datum. */
export function dueLabel(due: IsoDate | null, today: IsoDate, lang: Lang = "de"): string {
  if (!due) return tr(lang, "Offen", "Open");
  if (due < today) return tr(lang, "Überfällig", "Overdue");
  if (due === today) return tr(lang, "Heute", "Today");
  if (due === addDays(today, 1)) return tr(lang, "Morgen", "Tomorrow");
  return dateOf(due, lang);
}

/**
 * Bis zur Abgabe ist der Creator am Zug; in „Freigabe Content“ liegt der Content schon bei Agentur bzw. Marke (Befund 9).
 * „Vertrag“ gehört dazu (CREATOR-4): eine Abgabefrist läuft auch, solange der Vertrag noch unterschrieben wird.
 */
const CONTENT_STATES = new Set(["contract", "concept", "production"]);
/** Kooperationen, in denen keine Abgabe mehr ansteht */
const CLOSED_DEAL_STATES = new Set(["done", "cancelled", "blocked"]);

/** Stand eines Vertrags aus Creator-Sicht, wie in „Deine Verträge“ (vertraege/daten.ts, meinStand; Befund 10). */
export function contractTask(c: CContract, signers: readonly CSigner[]): "sign" | "none" {
  if (c.status !== "sent" && c.status !== "partially_signed") return "none";
  const me = signers.find((x) => x.contract_id === c.id && x.role === "talent" && (x.capacity ?? "party") === "party");
  if (me?.declined || c.declined_at) return "none";
  if (c.talent_signs === false) return "none";
  if (!me || me.signed) return "none";
  if (c.deal_cancelled) return "none";
  // CREATOR-R3-2: minderjährig ohne gesetzliche Vertretung – erst unterschreiben, wenn die Agentur die Eltern einträgt
  if (c.guardian_required) return "none";
  return "sign";
}

export function creatorNextSteps(input: {
  today: IsoDate;
  deals: readonly CDeal[];
  periods: readonly CPeriod[];
  brands: ReadonlyMap<string, CBrand>;
  views: readonly TalentInvoiceView[];
  contracts?: readonly CContract[];
  signers?: readonly CSigner[];
  profileMissing?: readonly string[];
  lang?: Lang;
  /** Gutschriftverfahren: fehlende Rechnungen stellt die Agentur aus, kein To-do für den Creator */
  selfBilling?: boolean;
  /** Provisionsrechnungen der Agentur an den Creator (direkte Abrechnung, Befund 14) */
  agencyInvoices?: readonly CInvoice[];
  /** Betrag für die Beschreibung (Befund 25); ohne Angabe im deutschen bzw. englischen Format */
  money?: (cents: number, currency: string) => string;
  /** Beiträge der Kooperationen (0068): Entwurfs- und Postingfristen als Aufgaben (CREATOR-4) */
  deliverables?: readonly CDeliverable[];
  platformLabel?: (key: string) => string | null;
  /** Nur-Lesen-Phase der Agentur (VERTRAEGE-R3-09): keine Aufgabe „Vertrag unterschreiben“, die sicher scheitert */
  readOnly?: boolean;
}): Step[] {
  const { today } = input;
  const lang = input.lang ?? "de";
  const t = (de: string, en: string) => tr(lang, de, en);
  const money = input.money ?? ((c: number, cur: string) => formatMoney(c, (isCurrency(cur) ? cur : "EUR") as Currency, lang === "en" ? "en-GB" : "de-DE"));
  const deals = new Map(input.deals.map((d) => [d.id, d]));
  const periodsById = new Map(input.periods.map((p) => [p.id, p]));
  const brand = (d: CDeal | undefined) => (d ? input.brands.get(d.brand_id)?.name ?? t("Marke", "Brand") : t("Marke", "Brand"));
  const out: Step[] = [];
  const invoiceLabel = (v: TalentInvoiceView) => v.row.number ?? t("ohne Nummer", "no number");
  // Beschreibung mit Marke, Monat und Betrag statt nur der Nummer (Befund 25)
  const about = (v: TalentInvoiceView, d: CDeal | undefined) => {
    const p = v.row.period_id ? periodsById.get(v.row.period_id) : undefined;
    const month = p ? monthOf(p.month.slice(0, 7), lang) : v.row.revenue_month ? monthOf(v.row.revenue_month.slice(0, 7), lang) : null;
    return [brand(d), month, money(v.row.gross_cents, v.row.currency), invoiceLabel(v)].filter(Boolean).join(" · ");
  };

  for (const v of input.views) {
    const d = v.row.deal_id ? deals.get(v.row.deal_id) : undefined;
    // Gutschrift der Agentur: vor der Auszahlung prüfen (Befund Creator 11)
    if (v.row.self_billed && v.bucket === "action" && v.state.state !== "payout_overdue") {
      out.push({ key: `selfbill:${v.row.id}`, to: `/creator/rechnungen?rechnung=${v.row.id}`, title: t("Gutschrift prüfen", "Check self-billed invoice"), detail: `${invoiceLabel(v)} · ${brand(d)}`, due: null, tone: "info" });
      continue;
    }
    switch (v.state.state) {
      case "correction_needed":
        out.push({ key: `fix:${v.row.id}`, to: `/creator/rechnungen/einreichen?rechnung=${v.row.id}`, title: t("Rechnung korrigieren", "Correct invoice"), detail: about(v, d), due: null, tone: "error" });
        break;
      case "draft":
        out.push({ key: `draft:${v.row.id}`, to: `/creator/rechnungen/einreichen?rechnung=${v.row.id}`, title: t("Entwurf fertigstellen", "Finish draft"), detail: about(v, d), due: null, tone: "warning" });
        break;
      case "in_review":
        out.push({ key: `review:${v.row.id}`, to: `/creator/rechnungen?rechnung=${v.row.id}`, title: t("Rechnung in Prüfung", "Invoice in review"), detail: about(v, d), due: null, tone: "info", kind: "info" });
        break;
      case "send_overdue": {
        // Die Agentur ist mit der Auszahlung im Verzug (Befund 11)
        const since = typeof v.state.labelParams?.dueDate === "string" ? v.state.labelParams.dueDate : v.row.effective_due_date ?? v.row.document_due_date;
        const sinceText = since ? t(` · seit ${dateOf(since, lang)}`, ` · since ${dateOf(since, lang)}`) : "";
        out.push({ key: `late:${v.row.id}`, to: `/creator/rechnungen?rechnung=${v.row.id}`,
          title: v.row.kind === "talent_to_agency" ? t("Auszahlung überfällig", "Payout overdue") : t("Versand an die Marke überfällig", "Sending to the brand overdue"),
          detail: `${about(v, d)}${sinceText}`, due: null, tone: "warning" });
        break;
      }
      case "payout_overdue": {
        // Auszahlung nach Frist bzw. nach Zahlung der Marke ausgeblieben (Befund Creator 6): Marke, Monat, Betrag, seit wann
        const since = v.record.payoutDueDate ?? v.row.effective_due_date ?? v.row.document_due_date;
        const sinceText = since ? t(` · seit ${dateOf(since, lang)}`, ` · since ${dateOf(since, lang)}`) : "";
        out.push({ key: `late:${v.row.id}`, to: `/creator/rechnungen?rechnung=${v.row.id}`, title: t("Auszahlung überfällig", "Payout overdue"),
          detail: `${about(v, d)}${sinceText}`, due: null, tone: "warning" });
        break;
      }
      default:
        if (v.state.openPaymentCheck && inPaymentRun(v.record)) {
          out.push({ key: `pay:${v.row.id}`, to: `/creator/rechnungen?rechnung=${v.row.id}`, title: t("Zahlungseingang melden", "Report payment received"), detail: about(v, d), due: null, tone: "warning" });
        }
    }
  }

  const rows = input.views.map((v) => v.row);
  const liveByPeriod = new Set(input.views.filter((v) => v.row.period_id && !["withdrawn", "discarded", "cancelled", "draft"].includes(v.row.status)).map((v) => v.row.period_id as string));
  const draftByPeriod = new Set(input.views.filter((v) => v.row.period_id && v.row.status === "draft").map((v) => v.row.period_id as string));
  for (const p of input.periods) {
    const d = deals.get(p.deal_id);
    if (!d || d.archived_at) continue;
    const month = p.month.slice(0, 7);
    const wizard = `/creator/rechnungen/einreichen?kooperation=${d.id}&monat=${month}`;
    const amount = (p.amount_cents ?? 0) > 0 ? money(p.amount_cents ?? 0, d.currency) : null;
    if (p.invoice_requested_at && !liveByPeriod.has(p.id) && !draftByPeriod.has(p.id) && !["paid", "cancelled"].includes(p.status)) {
      if (!input.selfBilling) {
        out.push({ key: `request:${p.id}`, to: wizard, title: t("Rechnung einreichen", "Submit invoice"),
          detail: [brand(d), monthOf(month, lang), t(`angefordert am ${dateOf(p.invoice_requested_at, lang)}`, `requested on ${dateOf(p.invoice_requested_at, lang)}`)].join(" · "), due: null, tone: "warning" });
      }
    } else if (!input.selfBilling && !draftByPeriod.has(p.id) && monthNeedsInvoice(p, d, rows, today)) {
      // Befund 1: geliefert bzw. abgerechnet, aber ohne deine Rechnung; die Agentur hat (noch) nicht angefordert
      out.push({ key: `missing:${p.id}`, to: wizard, title: t(`Rechnung für ${monthOf(month, lang)} fehlt`, `Invoice for ${monthOf(month, lang)} missing`),
        // Bei „Nur ihren Anteil“ ist der Betrag schon dein Anteil (0079), nie das Honorar der Marke (CREATOR-1)
        detail: [brand(d), d.title, amount ? (amountsAreShare(d) ? t(`dein Anteil ${amount}`, `your share ${amount}`) : t(`Honorar ${amount}`, `fee ${amount}`)) : null,
          t("deine Agentur hat sie noch nicht angefordert; ohne Rechnung keine Auszahlung", "your agency has not requested it yet; no payout without an invoice")].filter(Boolean).join(" · "),
        due: null, tone: "warning" });
    }
    const deadline = p.content_deadline ?? (input.periods.filter((x) => x.deal_id === d.id).length === 1 ? d.content_deadline : null);
    const state = p.manual_status ?? d.status;
    // Mit Beiträgen läuft die Abgabe je Beitrag (Aufgaben unten); die Monatsfrist wäre doppelt
    const perDeliverable = (input.deliverables ?? []).some((x) => x.deal_id === d.id && x.status !== "cancelled" && (!x.period_id || x.period_id === p.id));
    if (deadline && !perDeliverable && CONTENT_STATES.has(state) && !CLOSED_DEAL_STATES.has(d.status)
      && deadline >= addDays(today, -7) && deadline <= addDays(today, 21)) {
      out.push({ key: `content:${p.id}`, to: `/creator/deals/${d.id}?monat=${month}`, title: t("Content abgeben", "Deliver content"), detail: `${brand(d)} · ${d.title}`, due: deadline,
        tone: deadline < today ? "error" : deadline <= addDays(today, 1) ? "warning" : "info" });
    }
    if (state === "content_approval" && !["done", "cancelled", "blocked"].includes(d.status) && month <= addMonths(today.slice(0, 7), 1)) {
      out.push({ key: `approval:${p.id}`, to: `/creator/deals/${d.id}?monat=${month}`, title: t("Content in Freigabe", "Content in approval"),
        detail: `${brand(d)} · ${monthOf(month, lang)} · ${t("deine Agentur bzw. die Marke ist am Zug", "your agency or the brand is up next")}`, due: null, tone: "info", kind: "info" });
    }
  }

  // CREATOR-4: Beiträge mit Fristen, unabhängig vom Arbeitsstand der Kooperation, solange sie nicht abgeschlossen, angehalten
  // oder storniert ist. Entwurf einreichen bzw. überarbeiten, freigegebene Beiträge posten; eingereichte liegen beim Team.
  const platform = input.platformLabel ?? ((k: string) => k);
  for (const x of input.deliverables ?? []) {
    const d = deals.get(x.deal_id);
    if (!d || d.archived_at || CLOSED_DEAL_STATES.has(d.status) || x.status === "draft_submitted") continue;
    const due = nextDue(x);
    if (!due || due.date > addDays(today, 21)) continue;
    const p = x.period_id ? periodsById.get(x.period_id) : undefined;
    const to = `/creator/deals/${d.id}${p ? `?monat=${p.month.slice(0, 7)}` : ""}#beitraege`;
    const name = deliverableLabel(x, platform, lang);
    // CREATOR-R2-1: Posten erst nach der Freigabe; ohne Entwurfsfrist zählt der Posttermin als späteste Abgabe des Entwurfs
    // PRAXIS-R3-5: ohne Vorabfreigabe gleich posten und live melden
    const direkt = x.approval_required === false && !x.draft_due;
    const title = (due.kind === "publish" && x.status === "approved") || direkt ? t("Beitrag posten und live melden", "Post and report live")
      : x.status === "changes_requested" ? t("Entwurf überarbeiten", "Revise draft") : t("Entwurf einreichen", "Submit draft");
    const postOn = !direkt && due.kind === "draft" && due.dateIs === "publish_on" ? t(`posten am ${dateOf(due.date, lang)} nach Freigabe`, `post on ${dateOf(due.date, lang)} after approval`) : null;
    out.push({ key: `deliverable:${x.id}`, to, title, detail: [brand(d), d.title, name, postOn].filter(Boolean).join(" · "), due: due.date,
      tone: due.date < today ? "error" : due.date <= addDays(today, 1) ? "warning" : "info" });
  }

  const signers = input.signers ?? [];
  for (const c of input.contracts ?? []) {
    if (!input.readOnly && contractTask(c, signers) === "sign") {
      out.push({ key: `contract:${c.id}`, to: `/creator/vertraege/${c.id}`, title: t("Vertrag unterschreiben", "Sign contract"), detail: c.title, due: null, tone: "info" });
    }
  }

  // Provisionsrechnung der Agentur an dich (direkte Abrechnung): du bist am Zug (Befund 14)
  for (const i of input.agencyInvoices ?? []) {
    if (i.status !== "sent" || i.paid_at) continue;
    const due = i.effective_due_date ?? i.document_due_date;
    const d = i.deal_id ? deals.get(i.deal_id) : undefined;
    // Überweisung gemeldet (CREATOR-R3-6): läuft, die Agentur bestätigt den Eingang
    if (i.talent_payment_response === "paid") {
      out.push({ key: `fee:${i.id}`, to: `/creator#provision`, title: t("Provision überwiesen · Agentur bestätigt", "Commission transferred · agency confirms"),
        detail: [i.number, money(feeRest(i), i.currency), d ? brand(d) : null].filter(Boolean).join(" · "), due: null, tone: "info", kind: "info" });
      continue;
    }
    out.push({ key: `fee:${i.id}`, to: `/creator#provision`, title: t("Provisionsrechnung bezahlen", "Pay commission invoice"),
      detail: [i.number, money(feeRest(i), i.currency), d ? brand(d) : null].filter(Boolean).join(" · "), due: due ?? null,
      tone: due && due < today ? "error" : "warning" });
  }

  if (input.profileMissing?.length) {
    out.push({ key: "profile", to: "/creator/profil", title: t("Profil vervollständigen", "Complete profile"), detail: input.profileMissing.map((f) => profileFieldLabel(f, lang)).join(", "), due: null, tone: "warning" });
  }
  const rank: Record<Tone, number> = { error: 0, warning: 1, info: 2, neutral: 3, success: 4 };
  return out.sort((a, b) => Number(a.kind === "info") - Number(b.kind === "info") || (a.due ?? "9999").localeCompare(b.due ?? "9999") || rank[a.tone] - rank[b.tone] || a.title.localeCompare(b.title, "de"));
}

/** Umfang des Exports (my_data_export, Fassung 2): steht so in LIESMICH.txt und auf der Seite „Konto“ (CREATOR-R2-4). */
export const EXPORT_SCOPE = {
  de: "Konto, Einstellungen und Zustimmungen, deine Profile bei allen Agenturen (auch früheren) mit Steuerstatus-Verlauf und Auszahlungsdaten, Kooperationen mit Monaten, Positionen und Buchungen, Beiträge mit Entwürfen und Kennzahlen, Rechnungen mit Positionen, Verträge samt Aufhebungsverträgen, Nachrichten, Kalenderantworten, Instagram-Daten und Media-Kit",
  en: "your account, settings and consents, your profiles at all agencies (including former ones) with tax status history and payout details, collaborations with months, items and bookings, content with drafts and metrics, invoices with items, contracts including termination agreements, messages, calendar responses, Instagram data and media kit",
} as const;

// ── Sprache der Mails je Agentur (CREATOR-R2-5) ─────────────────────────────

/** Wie app.talent_language (0041): eigene Wahl am Profil der Agentur → Portalsprache → Dokumentsprache der Agentur → Deutsch. */
export function effectiveMailLanguage(talentLanguage: string | null | undefined, portalLanguage: string | null | undefined, orgDefault?: string | null): Lang {
  const pick = (v: string | null | undefined) => (v === "de" || v === "en" ? v : null);
  return pick(talentLanguage) ?? pick(portalLanguage) ?? pick(orgDefault?.slice(0, 2)) ?? "de";
}

/**
 * Satz für Konto und Profil: „Portal und Mails deiner Agenturen: English“ nur, wenn es für alle stimmt; sonst je Agentur
 * („Portal: English. Mails: Studio Nordlicht Deutsch, Atelier Funke English“). Eine Agentur kann die Sprache ihrer Mails
 * unter Benachrichtigungen abweichend haben (vom Creator oder von der Agentur gesetzt).
 */
export function languageSummary(portal: Lang, agencies: readonly { orgName: string; language: Lang }[], lang: Lang): { uniform: boolean; text: string } {
  const t = (de: string, en: string) => tr(lang, de, en);
  // derselbe Name wie unter Benachrichtigungen: auf Englisch „German“, nicht „Deutsch“ (SICHT-CREATOR-28)
  const name = (l: Lang) => mailLanguageName(l, lang);
  const uniform = agencies.every((a) => a.language === portal);
  if (uniform) return { uniform, text: t(`Portal und Mails deiner Agenturen: ${name(portal)}.`, `Portal and emails from your agencies: ${name(portal)}.`) };
  const list = [...agencies].sort((a, b) => a.orgName.localeCompare(b.orgName, "de")).map((a) => `${a.orgName} ${name(a.language)}`).join(", ");
  return { uniform, text: t(`Portal: ${name(portal)}. Mails: ${list}.`, `Portal: ${name(portal)}. Emails: ${list}.`) };
}

// ── Profil ──────────────────────────────────────────────────────────────────

const PROFILE_FIELD: Record<string, ProfileField> = {
  street: "street", postal_code: "postalCode", city: "city", country: "country", iban: "iban",
  bank_account_holder: "accountHolder", bank_name: "bankName", bic: "bic",
};
const FIELD_LABEL: Record<string, [string, string]> = {
  street: ["Straße", "Street"], postalCode: ["PLZ", "Postcode"], city: ["Ort", "City"], country: ["Land", "Country"], "country:unknown": ["Land (unbekannt)", "Country (unknown)"],
  iban: ["IBAN", "IBAN"], accountHolder: ["Kontoinhaber", "Account holder"], bankName: ["Bank", "Bank"], bic: ["BIC", "BIC"], "iban:invalid": ["gültige IBAN", "valid IBAN"],
  issuerVatId: ["USt-IdNr.", "VAT ID"], issuerChUid: ["UID/MWST-Nr.", "UID/VAT no."],
};

/** Name der Steuer auf der Rechnung nach Land des Ausstellers (0069): Schweiz und Liechtenstein „MWST“, sonst „Umsatzsteuer“ */
export function vatWord(country: string | null | undefined, lang: Lang = "de"): string {
  const c = normalizeCountry(country ?? null);
  return lang === "en" ? "VAT" : c === "CH" || c === "LI" ? "MWST" : "Umsatzsteuer";
}

/** Steuerbegriffe je Land des Creators (Befund 21): DE Steuernummer/USt-IdNr., AT Steuernummer/UID, CH UID/MWST-Nr. */
export function taxIdsLabel(country: string | null | undefined, lang: Lang = "de"): string {
  const c = normalizeCountry(country ?? null);
  if (c === "CH" || c === "LI") return tr(lang, "UID/MWST-Nr.", "UID/VAT no.");
  const vat = taxIdLabel(c, lang);
  return tr(lang, `Steuernummer oder ${vat}`, `Tax number or ${vat}`);
}

/**
 * Steuertexte im Profil je Land des Creators (SAAS-R2-7): Schweiz und Liechtenstein „MWST-pflichtig“ bzw. „nicht
 * mehrwertsteuerpflichtig“ statt Regelbesteuerung und Kleinunternehmer; Beispiel und Hinweis zur Steuer-ID je Land (in der
 * Schweiz steht die UID bei MWST-Pflicht auf jeder Rechnung, auch im Inland).
 */
export function creatorTaxTexts(country: string | null | undefined, lang: Lang = "de") {
  const c = normalizeCountry(country ?? null);
  const ch = c === "CH" || c === "LI";
  return {
    regular: ch ? tr(lang, "MWST-pflichtig", "Registered for VAT") : tr(lang, "Regelbesteuerung", "Standard VAT"),
    smallShort: ch ? tr(lang, "nicht mehrwertsteuerpflichtig", "not registered for VAT") : tr(lang, "Kleinunternehmer", "small business"),
    regularShort: ch ? tr(lang, "MWST-pflichtig", "registered for VAT") : tr(lang, "Regelbesteuerung", "standard VAT"),
    vatIdHint: c === "CH" ? tr(lang, "Zum Beispiel CHE-123.456.789 MWST. Bist du MWST-pflichtig, gehört sie auf jede Rechnung.", "For example CHE-123.456.789 MWST. If you are registered for VAT, it belongs on every invoice.")
      : c === "LI" ? tr(lang, "Deine MWST-Nummer der Steuerverwaltung. Bist du MWST-pflichtig, gehört sie auf jede Rechnung.", "Your VAT number from the tax administration. If you are registered for VAT, it belongs on every invoice.")
        // SAAS-R3-8: in Österreich gehört die UID regelbesteuerter Aussteller auf jede Rechnung über 400 € brutto, auch im Inland
        : c === "AT" ? tr(lang, "Beginnt mit ATU, zum Beispiel ATU12345678. Bist du regelbesteuert, gehört sie auf jede Rechnung über 400 € brutto, auch im Inland.", "Starts with ATU, for example ATU12345678. If you charge VAT, it belongs on every invoice over €400 gross, also within Austria.")
          : tr(lang, "Beginnt mit dem Länderkürzel, zum Beispiel DE123456789. Brauchst du vor allem bei Rechnungen ins EU-Ausland.", "Starts with the country code, for example DE123456789. Mainly needed for invoices to other EU countries."),
    // Beispiel im Format des Landes (SAAS-R3-8, CREATOR-R3-8)
    taxNumberHint: c === "AT" ? tr(lang, "Die Steuernummer von deinem Finanzamt, zum Beispiel 12 345/6789.", "The tax number from your tax office, for example 12 345/6789.")
      : c === "DE" || !c ? tr(lang, "Die Nummer von deinem Finanzamt, zum Beispiel 12/345/67890.", "The number from your tax office, for example 12/345/67890.")
        : tr(lang, "Die Steuernummer deines Landes, falls du eine hast.", "The tax number of your country, if you have one."),
    fromSmall: ch ? tr(lang, "Ab diesem Tag bist du nicht mehr mehrwertsteuerpflichtig. Rechnungen davor behalten ihren Stand; spätere mit MWST zeigen wir dir vor dem Speichern.", "You are no longer registered for VAT from this day. Earlier invoices keep their status; we show you later ones with VAT before saving.")
      : tr(lang, "Ab diesem Tag gilt die Kleinunternehmerregelung. Rechnungen davor behalten ihren Stand; spätere mit Umsatzsteuer zeigen wir dir vor dem Speichern.", "The small-business scheme applies from this day. Earlier invoices keep their status; we show you later ones with VAT before saving."),
    fromRegular: ch ? tr(lang, "Ab diesem Tag weist du MWST aus, zum Beispiel ab dem Tag, an dem du die Umsatzgrenze erreichst, oder ab 1. Januar. Rechnungen davor behalten ihren Stand.", "From this day you charge VAT, for example from the day you reach the turnover threshold, or from 1 January. Earlier invoices keep their status.")
      : tr(lang, "Ab diesem Tag weist du Umsatzsteuer aus, zum Beispiel ab dem Umsatz, mit dem du die Grenze überschreitest, oder ab 1. Januar. Rechnungen davor behalten ihren Stand.", "From this day you charge VAT, for example from the sale that exceeds the threshold, or from 1 January. Earlier invoices keep their status."),
  };
}

/** Länderliste im Profil: Deutschland, Österreich und die Schweiz oben (wie in der Agentur), danach alphabetisch (SAAS-R2-7). */
export function sortCountriesDach<C extends { code: string; name: Record<Lang, string> }>(list: readonly C[], lang: Lang = "de"): C[] {
  const top = ["DE", "AT", "CH"];
  return [...list].sort((a, b) => {
    const ia = top.indexOf(a.code), ib = top.indexOf(b.code);
    if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    return a.name[lang].localeCompare(b.name[lang], lang);
  });
}

/** Bezeichnung eines Profilfelds; Steuerfelder je Land des Creators. */
export function profileFieldLabel(key: string, lang: Lang = "de", country?: string | null): string {
  if (key === "issuerTaxNumberOrVatId") return taxIdsLabel(country, lang);
  if (key === "issuerVatId" && country) return taxIdLabel(normalizeCountry(country), lang);
  const l = FIELD_LABEL[key];
  return l ? tr(lang, l[0], l[1]) : key;
}

/**
 * Ab welchem Betrag die Agentur ohne Steuernummer nachfragt (Kleinbetragsgrenze der Fachlogik je Ausstellerland und
 * Währung, Befund 21). Ohne feste Grenze im Land: bei jeder Rechnung.
 */
export function taxIdThresholdText(country: string | null | undefined, lang: Lang = "de"): string {
  const rule = smallAmountRules().find((r) => normalizeCountry(country ?? null) === r.issuerCountry);
  if (rule) {
    const amount = formatMoney(rule.maxGrossCents, rule.currency, lang === "en" ? "en-GB" : "de-DE").replace(/[.,]00(?=\D|$)/, "");
    return tr(lang, `bei Rechnungen über ${amount}`, `for invoices above ${amount}`);
  }
  return tr(lang, "bei Rechnungen", "on invoices");
}

/**
 * Pflichtprofil vor der ersten Rechnung (TA-151, ST-22): Adresse und Bank nach den Plattform-Pflichtfeldern.
 * Fehlende Steuernummern sind nur ein Hinweis, nie eine Sperre (ST-08).
 */
export function profileGaps(talent: TalentSelf, payout: CPayout | null, rules: CreatorRules): { blocking: string[]; hints: string[] } {
  const required = rules.profileRequirements.map((k) => PROFILE_FIELD[k]).filter((f): f is ProfileField => !!f);
  const r = checkIssuerProfile(
    {
      street: talent.street, postalCode: talent.postal_code, city: talent.city, country: talent.country,
      iban: payout?.iban ?? null, accountHolder: payout?.bank_account_holder ?? null, bankName: payout?.bank_name ?? null, bic: payout?.bic ?? null,
      smallBusiness: talent.small_business, vatId: talent.vat_id, taxNumber: talent.tax_number,
    },
    required,
  );
  const tax = new Set(["issuerTaxNumberOrVatId", "issuerVatId", "issuerChUid"]);
  // Auszahlung per PayPal/Wise (Mail) oder SWIFT mit Kontonummer braucht keine IBAN (0042)
  const method = payout?.payout_method ?? "sepa";
  const covered = new Set<string>();
  if ((method === "paypal" || method === "wise") && payout?.payout_email?.trim()) ["iban", "accountHolder", "bankName", "bic"].forEach((f) => covered.add(f));
  if (method === "swift" && payout?.account_number?.trim()) covered.add("iban");
  const blocking = r.missing.filter((m) => !tax.has(m) && !covered.has(m));
  const hints = r.missing.filter((m) => tax.has(m));
  // Praxistest K-057: Die Prüfung der Agentur verlangt Steuernummer oder USt-IdNr. immer (Pflichtangabe auf der Rechnung), auch wenn
  // die Agentur sie nicht als Profil-Pflichtfeld führt; der Creator sieht den Hinweis deshalb unabhängig von dieser Einstellung
  if (!talent.tax_number?.trim() && !talent.vat_id?.trim() && !hints.some((h) => tax.has(h))) hints.push("issuerTaxNumberOrVatId");
  // Eine formal falsche IBAN ist ein Hinweis (die Auszahlung prüft die Agentur), keine Sperre der Rechnung
  if (payout?.iban && !isValidIban(payout.iban)) hints.push("iban:invalid");
  return { blocking: [...new Set(blocking)], hints };
}

/** Social-Handle ohne „@“ und ohne Link-Anteil; null für leer. */
export function normalizeHandle(input: string): string | null {
  const s = input.trim().replace(/^https?:\/\/(www\.)?[^/]+\//i, "").replace(/^@+/, "").replace(/\/+$/, "");
  return s === "" ? null : s;
}
export const HANDLE_PATTERN = {
  instagram: /^[A-Za-z0-9._]{1,30}$/,
  tiktok: /^[A-Za-z0-9._]{1,30}$/,
  youtube: /^[A-Za-z0-9._-]{1,100}$/,
} as const;

/** WhatsApp-Nummer ins internationale Format (+E.164, WA-01-Z); null bei ungültiger Eingabe. */
export function normalizePhone(input: string): string | null {
  let s = input.trim().replace(/[\s()/.-]/g, "");
  if (s.startsWith("00")) s = `+${s.slice(2)}`;
  return /^\+[1-9][0-9]{7,14}$/.test(s) ? s : null;
}

export function bankFindings(p: { iban?: string | null; bic?: string | null }, lang: Lang = "de"): string[] {
  const out: string[] = [];
  if (p.iban && p.iban.trim() && !isValidIban(p.iban)) out.push(tr(lang, "Die IBAN ist ungültig (Prüfziffer stimmt nicht).", "The IBAN is invalid (check digits do not match)."));
  else if (p.iban && isQrIban(p.iban)) out.push(tr(lang, "Das ist eine QR-IBAN. Sie nimmt nur Zahlungen mit QR-Referenz an, keine Überweisungen. Bitte die normale IBAN deines Kontos eintragen.", "This is a QR-IBAN. It only accepts payments with a QR reference, not transfers. Please enter the normal IBAN of your account."));
  if (p.bic && p.bic.trim() && !isValidBic(p.bic)) out.push(tr(lang, "Die BIC hat nicht das übliche Format (8 oder 11 Zeichen).", "The BIC does not have the usual format (8 or 11 characters)."));
  return out;
}

export function vatHint(value: string | null, country: string | null, lang: Lang = "de"): string | null {
  const f = checkVatIdFormat(value, country);
  if (!f) return null;
  const label = taxIdLabel(normalizeCountry(country), lang);
  if (f.code === "VAT_ID_LOOKS_LIKE_TAX_NUMBER") return tr(lang, "Das sieht nach einer Steuernummer aus. Trage sie unter „Steuernummer“ ein.", "This looks like a tax number. Please enter it under “Tax number”.");
  if (f.code === "VAT_ID_FORMAT") return tr(lang, `Ungewohntes Format der ${label} (erwartet: ${f.params?.expected ?? "Länderkürzel + Nummer"}).`, `Unusual format for the ${label} (expected: ${f.params?.expected ?? "country code + number"}).`);
  return tr(lang, `Die ${label} beginnt mit dem Länderkürzel, zum Beispiel ATU… oder FR….`, `The ${label} starts with the country code, for example ATU… or FR….`);
}

export interface ProfileForm {
  first_name: string;
  last_name: string;
  company_name?: string;
  display_name: string;
  nickname: string;
  email: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  small_business: boolean;
  vat_id: string;
  tax_number: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  whatsapp_phone: string;
  whatsapp_opt_in: boolean;
  holder: string;
  iban: string;
  bic: string;
  bank_name: string;
  bank_country: string;
  language: string;
  desktop_notifications: boolean;
}


export const orNull = (s: string) => (s.trim() === "" ? null : s.trim());

export type PayoutMethod = "sepa" | "swift" | "paypal" | "wise";

/**
 * Auszahlungswege (0042) sind wählbar, auch wenn noch keine Bankdaten gespeichert sind (0186): Vorher sah ein neuer
 * Creator nur die IBAN und konnte PayPal, Wise oder SWIFT erst nach dem ersten Speichern wählen. Nur eine gespeicherte
 * Zeile ohne die Spalte (Datenbank vor 0042) bleibt bei der IBAN.
 */
export function payoutMethodsAvailable(payout: object | null | undefined): boolean {
  return !payout || "payout_method" in payout;
}

/**
 * Bankdaten zum Speichern, je Auszahlungsweg bereinigt wie im Agentur-Profil (stammdaten/model.ts): IBAN, BIC und Bank
 * nur bei SEPA und SWIFT, Kontonummer und Routing nur bei SWIFT, die Auszahlungs-Mail nur bei PayPal und Wise. So bleibt
 * nach einem Wechsel keine alte Mail oder IBAN stehen, die der Auszahlungslauf übernehmen könnte (0186).
 */
export function creatorPayoutPatch(
  f: { holder: string; iban: string; bic: string; bank_name: string; bank_country: string; payout_method: PayoutMethod; account_number: string; routing_code: string; payout_email: string },
  methods: boolean,
): Record<string, string | null> {
  const base = {
    iban: orNull(f.iban.replace(/\s+/g, " ").toUpperCase()), bic: orNull(f.bic.toUpperCase()), bank_account_holder: orNull(f.holder), bank_name: orNull(f.bank_name), bank_country: orNull(f.bank_country),
  };
  if (!methods) return base;
  const m = f.payout_method;
  const bank = m === "sepa" || m === "swift";
  return {
    ...base,
    ...(bank ? {} : { iban: null, bic: null, bank_name: null }),
    payout_method: m,
    account_number: m === "swift" ? orNull(f.account_number.replace(/\s+/g, "")) : null,
    routing_code: m === "swift" ? orNull(f.routing_code.replace(/\s+/g, "")) : null,
    payout_email: bank ? null : orNull(f.payout_email.toLowerCase()),
  };
}

/** Eingaben prüfen; Fehler blockieren das Speichern, Hinweise nie (ST-08). */
export function validateProfile(f: ProfileForm, stored: { iban?: string | null; bic?: string | null } = {}, lang: Lang = "de"): { errors: string[]; hints: string[] } {
  const t = (de: string, en: string) => tr(lang, de, en);
  const errors: string[] = [];
  const hints: string[] = [];
  if (!f.first_name.trim()) errors.push(t("Bitte deinen Vornamen angeben.", "Please enter your first name."));
  // Nachtest CO-013: Rechnungen, Gutschriften und Verträge brauchen den vollen Namen, außer es wird über eine Firma abgerechnet
  if (!f.last_name.trim() && !f.company_name?.trim()) errors.push(t("Bitte deinen Nachnamen angeben. Er steht auf Rechnungen und Verträgen.", "Please enter your last name. It appears on invoices and contracts."));
  if (f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) errors.push(t("Die E-Mail-Adresse ist ungültig.", "The email address is invalid."));
  for (const [key, label] of [["instagram", "Instagram"], ["tiktok", "TikTok"], ["youtube", "YouTube"]] as const) {
    const h = normalizeHandle(f[key]);
    if (h && !HANDLE_PATTERN[key].test(h)) errors.push(t(`Der ${label}-Name enthält ungültige Zeichen.`, `The ${label} name contains invalid characters.`));
  }
  if (f.whatsapp_phone.trim() && !normalizePhone(f.whatsapp_phone)) errors.push(t("Die WhatsApp-Nummer bitte international angeben, zum Beispiel +49 151 23456789.", "Please enter the WhatsApp number in international format, for example +44 7700 900123."));
  if (f.whatsapp_opt_in && !normalizePhone(f.whatsapp_phone)) errors.push(t("Für WhatsApp-Hinweise brauchst du eine gültige Handynummer.", "WhatsApp notifications need a valid mobile number."));
  // Bankdaten nur prüfen, wenn sie geändert werden: gespeicherte Altwerte sperren das Speichern anderer Angaben nicht
  const same = (a: string, b: string | null | undefined) => a.replace(/\s+/g, "").toUpperCase() === (b ?? "").replace(/\s+/g, "").toUpperCase();
  errors.push(...bankFindings({ iban: same(f.iban, stored.iban) ? null : f.iban, bic: same(f.bic, stored.bic) ? null : f.bic }, lang));
  if (f.iban.trim() && same(f.iban, stored.iban) && !isValidIban(f.iban)) hints.push(t("Die gespeicherte IBAN ist formal ungültig. Bitte prüfen.", "The saved IBAN is formally invalid. Please check it."));
  if (!f.small_business && !f.vat_id.trim() && !f.tax_number.trim()) {
    const label = taxIdsLabel(orNull(f.country), lang);
    hints.push(t(`Ohne ${label} fragt deine Agentur ${taxIdThresholdText(orNull(f.country), lang)} nach.`, `Without a ${label.toLowerCase()} your agency will ask ${taxIdThresholdText(orNull(f.country), lang)}.`));
  }
  const v = vatHint(orNull(f.vat_id), orNull(f.country), lang);
  if (v) hints.push(v);
  return { errors, hints };
}

// ── Rechnungs-Assistent ─────────────────────────────────────────────────────

export type AmountMode = "budget" | "share";

export interface DraftLine {
  key: string;
  description: string;
  quantity: number;
  unitCents: number;
  commissionFree: boolean;
  /**
   * Steuerprüfung L12 (Abnahme E-2): eigener Steuersatz der Position in Basispunkten (1900 = 19 %, 700 = 7 %). Leer = der Satz
   * des Steuerfalls. Gilt nur, wenn der Steuerfall überhaupt Steuer ausweist; Kleinunternehmer und Reverse Charge bleiben bei 0.
   */
  rateBp?: number | null;
}

/**
 * Wofür die Rechnung ist (Befund 18): ein Monat der Kooperation, eine Nachberechnung zur Kooperation ohne Monat
 * (Verlängerung der Nutzungsrechte, Bonus) oder Auslagen (provisionsfrei, mit Beleg). „other“ (PARITAET-5): sonstige
 * Rechnung an die Agentur ohne Kooperation (z. B. ein selbst vermittelter Auftrag, den die Agentur noch nicht angelegt hat),
 * mit Pflichtbeschreibung; die Agentur ordnet sie bei der Prüfung zu.
 */
export type DraftPurpose = "month" | "extra" | "expenses" | "other";

/** Mindestlänge der Beschreibung einer Rechnung ohne Kooperation (wie die Datenbank, 0080) */
export const OTHER_NOTE_MIN = 10;

export interface InvoiceDraft {
  invoiceId: string | null;
  purpose?: DraftPurpose;
  dealId: string | null;
  periodId: string | null;
  month: MonthKey | null;
  number: string;
  issueDate: IsoDate;
  serviceFrom: IsoDate | null;
  serviceTo: IsoDate | null;
  termDays: number | null;
  /** budget = Kooperationsbudget eintragen, die Provision wird abgezogen (TA-083); share = eigener Anteil ohne Abzug */
  mode: AmountMode;
  commissionPercent: number | null;
  lines: DraftLine[];
  currency: string;
  /** Wofür die Rechnung ist, in eigenen Worten (Pflicht ohne Kooperation) */
  note?: string;
}

export interface DraftComputation {
  totals: InvoiceTotals;
  tax: TaxTreatment;
  notice: string;
  commissionPercent: number;
  /** Steuersatz je berechneter Position (gleiche Reihenfolge wie totals.lines), in Basispunkten */
  lineRatesBp?: number[];
  /** Steuer je Satz, wenn die Positionen verschiedene Sätze tragen (L2: § 14 Abs. 4 Satz 1 Nr. 7 und 8 UStG); sonst null */
  byRate?: { ratePercent: number; netCents: number; vatCents: number }[] | null;
}

/**
 * Positionen nach dem Speichern (Schritt 2 → 3): gespeichert ist der eigene Anteil (nach Provision), weiteres Bearbeiten ohne
 * erneuten Abzug. Abnahme E-5: mit dem Satz je Position, wie gespeichert; ohne ihn rechneten Schritt 3 und 4 bis zum Neuladen
 * alles zum Satz des Steuerfalls (1.309 € statt 1.297 €).
 */
export function linesAfterSave(comp: DraftComputation): DraftLine[] {
  return comp.totals.lines.map((l, i) => ({
    key: newLineKey(), description: l.description, quantity: l.quantity, unitCents: l.unitPriceCents, commissionFree: l.commissionFree,
    ...(typeof comp.lineRatesBp?.[i] === "number" && comp.tax.ratePercent > 0 ? { rateBp: comp.lineRatesBp[i] } : {}),
  }));
}

/** Wählbare Steuersätze je Position (Abnahme E-2), aus der Fachlogik (vatRateChoices), in Basispunkten */
export const creatorRateOptions = vatRateChoices;

/**
 * Steuer je Satz wie die Datenbank (app.invoice_totals_guard: je Satz gerundet) und der Beleg (vatBreakdown): Summe der
 * Positionen je Satz, darauf die Steuer kaufmännisch gerundet.
 */
export function vatByRate(lines: readonly { netCents: number; rateBp: number }[]): { vatCents: number; rows: { ratePercent: number; netCents: number; vatCents: number }[] } {
  const net = new Map<number, number>();
  for (const l of lines) net.set(l.rateBp, (net.get(l.rateBp) ?? 0) + l.netCents);
  const rows = [...net.entries()].sort((a, b) => b[0] - a[0]).map(([bp, n]) => ({ ratePercent: bp / 100, netCents: n, vatCents: Math.sign(n) * Math.round(Math.abs(n) * bp / 10_000) }));
  return { vatCents: rows.reduce((a, r) => a + r.vatCents, 0), rows };
}

const toLines = (d: InvoiceDraft): LineInput[] =>
  d.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitCents, commissionFree: l.commissionFree }));

/** Summen und Steuer wie im Dokumentmodell (zwei Durchläufe: Satz, dann mit echtem Brutto für die Kleinbetragsgrenze). */
export function computeDraft(d: InvoiceDraft, talent: TalentSelf, org: OrgAddress, recipient?: Pick<DraftRecipient, "country" | "vatId" | "isBusiness"> & { language?: "de" | "en" } | null): DraftComputation {
  const commissionPercent = d.mode === "budget" ? Math.min(100, Math.max(0, d.commissionPercent ?? 0)) : 0;
  const lines = toLines(d);
  const currency = (isCurrency(d.currency) ? d.currency : "EUR") as Currency;
  // Steuer gegen den tatsächlichen Empfänger: die Agentur oder bei direkter Abrechnung die Marke
  const to = recipient ?? { country: org.country, vatId: org.vat_id, isBusiness: true };
  const taxInput = (gross: number) => ({
    issuerCountry: talent.country, issuerSmallBusiness: talent.small_business, issuerVatId: talent.vat_id, issuerTaxNumber: talent.tax_number,
    recipientCountry: to.country, recipientIsBusiness: to.isBusiness, recipientVatId: to.vatId, currency, grossCents: gross,
    // K-311: Steuersatz nach Leistungsdatum (B.1); eingetragener Leistungszeitraum vor dem abgerechneten Monat (eingelesene
    // Rechnungen ohne Monat meldeten sonst „Der Leistungszeitraum ist Pflicht.“, Abnahme 25.10.2026)
    serviceDate: d.serviceTo ?? d.serviceFrom ?? (d.month ? `${String(d.month).slice(0, 7)}-01` : null),
  });
  const net0 = computeInvoiceTotals(lines, { vatRatePercent: 0, commissionPercent });
  // Abnahme E-2: Steuer je Position (eigener Satz oder der des Steuerfalls), je Satz gerundet; ohne Steuerausweis alles 0
  const withRates = (ratePercent: number) => {
    const rates = net0.lines.map((c) => {
      const own = d.lines[c.index]?.rateBp;
      return ratePercent > 0 ? (typeof own === "number" && own >= 0 ? Math.round(own) : Math.round(ratePercent * 100)) : 0;
    });
    const v = vatByRate(net0.lines.map((c, i) => ({ netCents: c.lineTotalCents, rateBp: rates[i]! })));
    return { totals: { ...net0, vatCents: v.vatCents, grossCents: net0.netCents + v.vatCents }, rates, rows: v.rows };
  };
  const first = determineTaxTreatment(taxInput(net0.netCents));
  const pass1 = withRates(first.ratePercent);
  const tax = determineTaxTreatment(taxInput(pass1.totals.grossCents));
  const pass = tax.ratePercent === first.ratePercent ? pass1 : withRates(tax.ratePercent);
  // Steuerhinweis in der Sprache des Belegs (Empfängersprache, Befund 14)
  return {
    // Abnahme E-7: bei mehreren Sätzen „Enthält Umsatzsteuer (19 % und 7 %).“
    totals: pass.totals, tax, notice: renderTaxNoticeForRates(tax.noticeKey, recipient?.language ?? "de", pass.rates.length ? pass.rates.map((bp) => bp / 100) : [tax.ratePercent]), commissionPercent,
    lineRatesBp: pass.rates, byRate: pass.rows.length > 1 ? pass.rows : null,
  };
}

/** Wertevorrat für invoices.tax_mode (Fachlogik, 0044) */
export { TAX_MODE_DB };

export interface Message {
  key: string;
  text: string;
}
export interface DraftCheck {
  errors: Message[];
  questions: Message[];
  hints: Message[];
}

export interface DraftEnv {
  today: IsoDate;
  timeZone: string;
  talent: TalentSelf;
  payout: CPayout | null;
  org: OrgAddress;
  /** Empfänger der Rechnung (Agentur oder bei direkter Abrechnung die Marke) */
  recipient?: DraftRecipient | null;
  rules: CreatorRules;
  invoices: readonly CInvoice[];
  deal: CDeal | null;
  period: CPeriod | null;
  extras: readonly CLineItem[];
  hasPdf: boolean;
  /** Wirksamer Satz der Kooperation inklusive Agentur-Standard (talent_deal_rates) */
  rate?: CommissionInfo | null;
  /** Eigene Nummern aus allen Agenturen (Befund 10) */
  ownNumbers?: readonly OwnNumber[];
  /** Auslagen brauchen einen Beleg neben der Rechnung */
  hasReceipt?: boolean;
  /** Herkunft der Rechnungsdatei und Bestätigung bei eigener Datei (0045) */
  pdfOrigin?: "generated" | "uploaded" | null;
  pdfConfirmed?: boolean;
  lang?: Lang;
}

/** Klartext zu Befunden der Fachlogik. */
export function findingText(f: Finding, lang: Lang = "de", ctx: { country?: string | null; recipientIsAgency?: boolean } = {}): string {
  const t = (de: string, en: string) => tr(lang, de, en);
  const p = f.params ?? {};
  const monthText = (m: string) => monthOf(m, lang);
  const pct = (v: unknown) => numOf(Number(v), lang);
  switch (f.code) {
    case "NUMBER_MONTH_MISMATCH": return t(`Der Monat in der Rechnungsnummer (${String(p.numberMonth).split(",").map(monthText).join(", ")}) passt nicht zum Rechnungsdatum (${monthText(String(p.invoiceMonth))}).`,
      `The month in the invoice number (${String(p.numberMonth).split(",").map(monthText).join(", ")}) does not match the invoice date (${monthText(String(p.invoiceMonth))}).`);
    case "NUMBER_DOES_NOT_MATCH_SCHEME": return t(`Die Rechnungsnummer folgt nicht dem Schema ${String(p.template)}.`, `The invoice number does not follow the pattern ${String(p.template)}.`);
    case "INVOICE_DATE_MOVED_TO_WORKDAY": return t(`Das Rechnungsdatum fällt auf ein Wochenende. Nächster Werktag: ${dateOf(String(p.to), lang)}.`, `The invoice date falls on a weekend. Next working day: ${dateOf(String(p.to), lang)}.`);
    case "INVOICE_DATE_IN_FUTURE": return t(`Das Rechnungsdatum liegt ${p.days} Tage in der Zukunft.`, `The invoice date is ${p.days} days in the future.`);
    case "INVOICE_DATE_OLDER_THAN_60_DAYS": return t(`Das Rechnungsdatum liegt mehr als 60 Tage zurück (${p.days} Tage).`, `The invoice date is more than 60 days ago (${p.days} days).`);
    case "SERVICE_DATE_REQUIRED": return t("Bitte den Leistungszeitraum angeben.", "Please enter the service period.");
    case "SERVICE_PERIOD_INVERTED": return t("Der Leistungszeitraum endet vor seinem Beginn.", "The service period ends before it starts.");
    case "SERVICE_DATE_IN_FUTURE": return t("Der Leistungszeitraum reicht in die Zukunft. Trotzdem einreichen?", "The service period extends into the future. Submit anyway?");
    case "SERVICE_DATE_OUTSIDE_BILLED_MONTH": return t(`Der Leistungszeitraum liegt nicht im abgerechneten Monat ${monthText(String(p.billedMonth))}. Trotzdem einreichen?`, `The service period is outside the billed month ${monthText(String(p.billedMonth))}. Submit anyway?`);
    case "SERVICE_DATE_NEAR_INVOICE_DATE": return t("Leistungs- und Rechnungsdatum liegen dicht beieinander, die Leistung aber nicht im abgerechneten Monat.", "Service and invoice date are close together, but the service is not in the billed month.");
    case "BUDGET_DEVIATION": return t(`Die Positionen weichen um ${pct(p.percent)} % vom vereinbarten Betrag ab.`, `The items differ from the agreed amount by ${pct(p.percent)} %.`);
    case "LINE_LOOKS_LIKE_EXPENSE": return t(`Position ${p.line} sieht nach einer Auslage aus („${p.keyword}“). Auslagen sind provisionsfrei; setze das Häkchen, wenn es eine ist.`,
      `Item ${p.line} looks like an expense (“${p.keyword}”). Expenses are commission-free; tick the box if it is one.`);
    case "SHORT_LINE_DESCRIPTIONS": return t(`Beschreibe die Leistung in Position ${String(p.lines).replace(/,/g, ", ")} genauer (mindestens ${p.minChars} Zeichen), sonst fragt deine Agentur nach.`,
      `Describe the service in item ${String(p.lines).replace(/,/g, ", ")} in more detail (at least ${p.minChars} characters), otherwise your agency will ask.`);
    case "LINE_OMITTED": return t(`Position ${p.line} hat keine Menge oder keinen Preis und erscheint nicht auf der Rechnung.`, `Item ${p.line} has no quantity or price and will not appear on the invoice.`);
    case "MISSING_TAX_FIELD": {
      const field = String(p.field);
      if (field === "serviceDate") return t("Der Leistungszeitraum ist Pflicht.", "The service period is required.");
      if (field === "recipientVatId") return ctx.recipientIsAgency === false
        ? t("Die USt-IdNr. der Marke fehlt in den Rechnungsangaben. Frag deine Agentur danach.", "The brand's VAT ID is missing from the billing details. Ask your agency for it.")
        : t("Die USt-IdNr. deiner Agentur fehlt in ihren Angaben.", "Your agency's VAT ID is missing from its details.");
      // Bezeichnung ohne angehängten Punkt („USt-IdNr..“, Befund 22)
      const label = profileFieldLabel(field, lang, ctx.country).replace(/\.$/, "");
      return t(`Für diese Rechnung brauchst du: ${label}. Du kannst trotzdem einreichen.`, `This invoice needs: ${label}. You can still submit it.`);
    }
    case "VAT_ID_FORMAT":
    case "VAT_ID_LOOKS_LIKE_TAX_NUMBER":
    case "VAT_ID_MUST_START_WITH_COUNTRY": return t("Das Format einer USt-IdNr. sieht ungewohnt aus. Prüfe dein Profil.", "The format of a VAT ID looks unusual. Please check your profile.");
    case "ISSUER_COUNTRY_UNKNOWN": return t("Dein Land fehlt im Profil. Die Steuer lässt sich so nicht bestimmen; bitte prüfen.", "Your country is missing from your profile. The tax cannot be determined; please check.");
    case "RECIPIENT_COUNTRY_UNKNOWN": return ctx.recipientIsAgency === false
      ? t("Das Land der Marke ist unbekannt. Die Steuer bitte von Hand prüfen.", "The brand's country is unknown. Please check the tax manually.")
      : t("Das Land deiner Agentur ist unbekannt. Die Steuer bitte von Hand prüfen.", "Your agency's country is unknown. Please check the tax manually.");
    case "NO_TAX_RULE":
    case "NO_RATE_DATA": return t("Für diese Kombination gibt es keine feste Steuerregel. Bitte die Steuer auf deiner Rechnung selbst prüfen.", "There is no fixed tax rule for this combination. Please check the tax on your invoice yourself.");
    case "RATE_CATEGORY_UNKNOWN": return t("Unbekannte Steuersatz-Kategorie.", "Unknown tax rate category.");
    // K-269: ohne MWST-Nummer (CHE-… MWST) keine MWST auf der Rechnung
    case "CH_VAT_NUMBER_MISSING": return t("Ohne MWST-Nummer (z. B. „CHE-123.456.789 MWST“) stellst du die Rechnung ohne MWST aus. Bist du im MWST-Register eingetragen, trag die Nummer mit dem Zusatz „MWST“ im Profil ein.", "Without a VAT number (e.g. “CHE-123.456.789 MWST”) your invoice has no VAT. If you are registered for Swiss VAT, add the number with the suffix “MWST” in your profile.");
    default: return f.code;
  }
}

/** Befunde je Schwere einsortieren (error → Fehler, question → Rückfrage, sonst Hinweis). */
function sortFinding(out: DraftCheck, f: Finding, key: string, lang: Lang = "de", ctx: { country?: string | null; recipientIsAgency?: boolean } = {}) {
  const m = { key, text: findingText(f, lang, ctx) };
  if (f.severity === "error") out.errors.push(m);
  else if (f.severity === "question") out.questions.push(m);
  else out.hints.push(m);
}

/**
 * Prüfungen wie im bisherigen Rechnungs-Generator (TA-085): Pflichtangaben, Nummer (eindeutig, Monat passend),
 * Datum, Leistungszeitraum, Positionen, Auslagen, Provision, Abweichung vom Betrag, Dublette, Steuer.
 * Rückfragen blockieren das Einreichen, bis sie bestätigt sind (openQuestions); Hinweise nie.
 */
export function checkDraft(d: InvoiceDraft, env: DraftEnv, stage: "save" | "submit"): DraftCheck {
  const lang = env.lang ?? "de";
  const t = (de: string, en: string) => tr(lang, de, en);
  const ctx = { country: env.talent.country, recipientIsAgency: !isDirect(env.deal) };
  const add = (f: Finding, key: string) => sortFinding(out, f, key, lang, ctx);
  const out: DraftCheck = { errors: [], questions: [], hints: [] };
  const comp = computeDraft(d, env.talent, env.org, env.recipient);
  const purpose = d.purpose ?? "month";
  if (!d.dealId && purpose !== "other") out.errors.push({ key: "deal", text: t("Bitte eine Kooperation wählen.", "Please choose a collaboration.") });
  if (purpose === "other" && (d.note ?? "").trim().length < OTHER_NOTE_MIN) {
    out.errors.push({ key: "note", text: t(`Beschreibe kurz, wofür die Rechnung ist (mindestens ${OTHER_NOTE_MIN} Zeichen). Deine Agentur ordnet sie damit zu.`,
      `Briefly describe what the invoice is for (at least ${OTHER_NOTE_MIN} characters). Your agency uses it to assign the invoice.`) });
  }
  if (purpose === "month" && (!d.periodId || !d.month)) out.errors.push({ key: "month", text: t("Bitte einen Leistungsmonat wählen.", "Please choose a service month.") });
  // Befund Creator 8: ein Monat, der noch nicht begonnen hat, lässt sich nicht abrechnen (vorher war es nur eine Rückfrage)
  if (purpose === "month" && d.month && d.month.slice(0, 7) > env.today.slice(0, 7)) {
    // GELD-R3-9: hat die Agentur genau diesen Monat vorab angefordert (Pauschale im Voraus, Ausfallhonorar), ist es eine
    // Rückfrage statt einer Sperre; sonst bleibt es gesperrt
    const requested = !!env.period?.invoice_requested_at && env.period.id === d.periodId;
    const m = requested
      ? { key: "month-future", text: t(`${monthOf(d.month, lang)} hat noch nicht begonnen. Deine Agentur hat die Rechnung vorab angefordert; reiche sie nur ein, wenn ihr eine Abrechnung im Voraus vereinbart habt.`,
        `${monthOf(d.month, lang)} has not started yet. Your agency requested the invoice in advance; only submit it if you agreed on billing in advance.`) }
      : { key: "month-future", text: t(`${monthOf(d.month, lang)} hat noch nicht begonnen. Rechnungen gibt es erst für erbrachte Leistungen; wähle einen vergangenen oder den laufenden Monat.`,
        `${monthOf(d.month, lang)} has not started yet. Invoices are only for work already done; choose a past or the current month.`) };
    if (stage === "submit") (requested ? out.questions : out.errors).push(m);
    else out.hints.push(m);
  }

  const gaps = profileGaps(env.talent, env.payout, env.rules);
  const label = (f: string) => profileFieldLabel(f, lang, env.talent.country);
  if (gaps.blocking.length) {
    out.errors.push({ key: "profile", text: t(`Dein Profil ist unvollständig (${gaps.blocking.map(label).join(", ")}). Ergänze es unter „Dein Profil“, dann kannst du Rechnungen einreichen.`,
      `Your profile is incomplete (${gaps.blocking.map(label).join(", ")}). Complete it under “Your profile”, then you can submit invoices.`) });
  }
  for (const h of gaps.hints) {
    out.hints.push(h === "iban:invalid"
      ? { key: "iban", text: t("Die IBAN in deinem Profil ist formal ungültig. Prüfe sie, damit deine Auszahlung ankommt.", "The IBAN in your profile is formally invalid. Check it so your payout arrives.") }
      : { key: `taxid:${h}`, text: t(`${label(h)} fehlt in deinem Profil. Das ist kein Hindernis, deine Agentur fragt aber vielleicht nach.`, `${label(h)} is missing from your profile. This does not block you, but your agency may ask.`) });
  }

  // Nummer: leer, doppelt (Fachlogik und Datenbank-Regel, auch über Agenturen hinweg), Monat passend zum Datum (NR-06)
  const number = d.number.trim();
  if (!normalizeInvoiceNumber(number)) out.errors.push({ key: "number", text: t("Bitte eine Rechnungsnummer eingeben.", "Please enter an invoice number.") });
  else {
    const records = env.invoices.map((i) => toRecord(i, env.timeZone)).filter((x): x is InvoiceRecord => !!x);
    // Fachlogik und Datenbank-Regel sind seit 0031 gleich: verworfen und zurückgezogen geben frei, storniert nicht (WS-06, A-02)
    const conflict = checkNumberConflict({ number, issuerId: env.talent.id, invoiceId: d.invoiceId }, records);
    const norm = normalizeInvoiceNumber(number);
    // Befund 10: dieselbe Person bei einer anderen Agentur (anderes Profil) – die Nummer gilt je Aussteller
    const elsewhere = (env.ownNumbers ?? []).find((n) => n.talent_id !== env.talent.id && n.invoice_id !== d.invoiceId && normalizeInvoiceNumber(n.number) === norm);
    if (!conflict.ok) {
      out.errors.push({ key: "number", text: t(`Die Nummer ${number} hast du schon vergeben. Jede Rechnungsnummer darf nur einmal vorkommen.`, `You have already used the number ${number}. Each invoice number may only be used once.`) });
    } else if (elsewhere) {
      out.errors.push({ key: "number", text: t(`Die Nummer ${number} hast du schon bei einer anderen Agentur vergeben${elsewhere.issue_date ? ` (Rechnung vom ${dateOf(elsewhere.issue_date, lang)})` : ""}. Deine Nummern gelten für dich als Aussteller, nicht je Agentur.`,
        `You have already used the number ${number} with another agency${elsewhere.issue_date ? ` (invoice dated ${dateOf(elsewhere.issue_date, lang)})` : ""}. Your numbers apply to you as the issuer, not per agency.`) });
    }
    const month = checkNumberMonth(number, d.issueDate, env.rules.numberScheme);
    if (month) add(month, "number-month");
  }

  const year = Number(d.issueDate.slice(0, 4));
  const dateCheck = checkInvoiceDate({ invoiceDate: d.issueDate, today: env.today, source: "user", workdaysOnly: env.rules.invoiceDateWorkdaysOnly,
    holidays: env.rules.invoiceDateWorkdaysOnly ? holidayDatesOrNone(env.rules.holidayRegion, [year, year + 1]) : [] });
  dateCheck.findings.forEach((f, i) => add(f, `date:${i}`));

  const service = checkServicePeriod({
    servicePeriod: d.serviceFrom && d.serviceTo ? { from: d.serviceFrom, to: d.serviceTo } : null,
    invoiceDate: d.issueDate,
    // Nachberechnung und Auslagen hängen an keinem Monat: der Zeitraum gilt, wie er ist
    billedMonth: purpose === "month" ? d.month : null,
    today: env.today,
    isDraft: stage === "save",
  });
  for (const f of service) add(f, f.severity === "question" ? "service" : `service:${f.code}`);

  if (d.termDays === null || !Number.isInteger(d.termDays) || d.termDays < 0 || d.termDays > 180) {
    out.errors.push({ key: "term", text: t("Bitte ein Zahlungsziel zwischen 0 und 180 Tagen angeben.", "Please enter payment terms between 0 and 180 days.") });
  }

  // Positionen
  if (comp.totals.lines.length === 0) out.errors.push({ key: "lines", text: t("Mindestens eine Position mit Menge und Preis ist nötig.", "At least one item with quantity and price is required.") });
  for (const i of comp.totals.omitted) add({ code: "LINE_OMITTED", severity: "hint", params: { line: i + 1 } }, `omitted:${i}`);
  for (const f of approvalQuestions(d.lines.map((l) => ({ description: l.description, unitPriceCents: l.unitCents, quantity: l.quantity })), env.rules.minLineDescriptionChars)) {
    add({ ...f, severity: "hint" }, "short");
  }
  if (purpose !== "expenses") {
    for (const f of detectExpenseLikeLines(toLines(d), env.rules.expenseKeywords)) add(f, `expense:${f.params?.line}`);
  }

  // Provision (TA-083): im Budget-Modus Pflicht; jede Abweichung vom wirksamen Satz (auch dem Agentur-Standard) ist eine Rückfrage
  const rate = env.rate !== undefined ? env.rate : env.deal ? commissionOf(env.deal, env.talent) : null;
  const pctText = (v: number) => pctOf(v, lang);
  if (d.mode === "budget") {
    if (d.commissionPercent === null || !(d.commissionPercent >= 0 && d.commissionPercent <= 100)) {
      out.errors.push({ key: "commission", text: t("Bitte die Provision deiner Agentur in Prozent angeben. Du findest sie in deinem Vertrag.", "Please enter your agency's commission in percent. You will find it in your contract.") });
    } else if (rate && !(env.deal && rateWithTiers(env.deal, rate, env.period?.month).tiers) && Math.abs(rate.percent - d.commissionPercent) > 0.001) {
      // Mit Staffel gibt es keinen einzelnen Satz; dort prüft die Summenprobe unten den Betrag
      out.questions.push({ key: "commission", text: t(`Dein Satz (${pctText(d.commissionPercent)}) weicht vom vereinbarten Satz (${pctText(rate.percent)}) ab. Trotzdem so abrechnen?`,
        `Your rate (${pctText(d.commissionPercent)}) differs from the agreed rate (${pctText(rate.percent)}). Invoice like this anyway?`) });
    }
  }

  // Summenprobe (GELD-1, CREATOR-8): der Rechnungsbetrag gegen den vereinbarten Betrag des Monats, gleich in welchem Eingabemodus.
  // Über die Agentur ist das dein Anteil (Satz oder Staffel, Abzug der Marke), direkt an die Marke deren Betrag (Befund 3). Weicht er
  // über die Toleranz ab, ist das eine Rückfrage vor dem Einreichen: dieselbe Prüfung, mit der die Agentur die Rechnung prüft.
  if (purpose === "month" && env.period && env.deal && env.deal.model !== "commission_only") {
    // Beträge schon als Anteil (0079): der vereinbarte Betrag ist bereits der Anteil; sonst Anteil mit Staffel und Abzug (0075)
    const expected = amountsAreShare(env.deal) && !isDirect(env.deal)
      ? (env.period.amount_cents ?? 0) + env.extras.reduce((a, e) => a + e.amount_cents, 0)
      : expectedInvoiceNetCents(env.deal, env.period.amount_cents ?? 0, env.extras, rate, env.period.month);
    if (expected != null) {
      const net = comp.totals.netCents;
      const passThrough = env.extras.filter((e) => !e.commissionable).reduce((a, e) => a + e.amount_cents, 0);
      const tolerance = Math.max(2, Math.round((Math.abs(expected) * env.rules.budgetDeviationPercent) / 100));
      // Wie die Agentur: ein Betrag ohne die durchlaufenden Posten (eigene Auslagenrechnung) gilt ebenfalls als stimmig
      if (Math.abs(net - expected) > tolerance && Math.abs(net - (expected - passThrough)) > tolerance) {
        const cur = (isCurrency(d.currency) ? d.currency : "EUR") as Currency;
        const m = (c: number) => formatMoney(c, cur, lang === "en" ? "en-GB" : "de-DE");
        out.questions.push({ key: "amount", text: t(
          `Dein Rechnungsbetrag (${m(net)} netto) weicht vom vereinbarten Betrag für ${monthOf(env.period.month.slice(0, 7), lang)} ab (${m(expected)} netto${isDirect(env.deal) ? "" : ", dein Anteil nach Provision"}). Deine Agentur fragt dann nach. Trotzdem so einreichen?`,
          `Your invoice amount (${m(net)} net) differs from the agreed amount for ${monthOf(env.period.month.slice(0, 7), lang)} (${m(expected)} net${isDirect(env.deal) ? "" : ", your share after commission"}). Your agency will ask about it. Submit anyway?`) });
      }
    } else if (d.mode === "budget") {
      // Ohne bekannten Satz: das eingetragene Budget gegen das vereinbarte Honorar (Hinweis)
      const agreed = (env.period.amount_cents ?? 0) + env.extras.reduce((a, e) => a + e.amount_cents, 0);
      const f = checkBudgetDeviation(comp.totals.rawNetCents, agreed, env.rules.budgetDeviationPercent);
      if (f) add(f, "budget");
    }
  }

  // RE-40: dieselbe Leistung schon abgerechnet
  if (purpose === "month" && d.periodId) {
    const others = env.invoices.filter((i) => i.id !== d.invoiceId && i.period_id === d.periodId && i.kind !== "agency_to_brand"
      && !["withdrawn", "discarded", "cancelled"].includes(i.status));
    if (others.length) {
      const list = others.map((o) => o.number ?? t("ohne Nummer", "no number")).join(", ");
      out.questions.push({ key: "duplicate", text: t(`Für diesen Monat gibt es schon ${others.length === 1 ? "die Rechnung" : "die Rechnungen"} ${list}. Wirklich eine weitere anlegen?`,
        `There is already ${others.length === 1 ? "an invoice" : "invoices"} for this month (${list}). Create another one?`) });
    }
  }

  // Steuer: Hinweise, nie Sperre (ST-08)
  comp.tax.findings.forEach((f, i) => add({ ...f, severity: f.severity === "error" ? "hint" : f.severity === "question" ? "hint" : f.severity }, `tax:${i}`));
  // 0181 (Anwalt Punkt 11, 23): Der Steuerfall ist ein Vorschlag aus Profil und Empfänger. Außer Inland regulär und Kleinunternehmer
  // laut Profil bestätigt der Creator ihn ausdrücklich; die Datenbank lehnt das Einreichen sonst ab (confirm:tax_treatment)
  if (stage === "submit" && taxTreatmentNeedsConfirmation({ mode: comp.tax.mode, manualReview: comp.tax.manualReview })) {
    out.questions.push({ key: TAX_CONFIRM_KEY, text: t(
      `Steuerlicher Vorschlag für diese Rechnung: ${taxModeText(comp.tax.mode, env.talent.country, env.recipient?.country ?? env.org.country)}. Er folgt aus deinem Steuerstatus im Profil und dem Empfänger; passt er nicht, ändere dein Profil oder sprich mit deiner Agentur. Bitte bestätige die steuerliche Behandlung dieser Rechnung. CreatorOffice stellt keine Steuerberatung dar.`,
      `Suggested tax treatment for this invoice: ${taxModeText(comp.tax.mode, env.talent.country, env.recipient?.country ?? env.org.country)}. It follows from the tax status in your profile and the recipient; if it does not fit, change your profile or talk to your agency. Please confirm the tax treatment of this invoice. CreatorOffice does not provide tax advice.`) });
  }

  if (stage === "submit" && !env.hasPdf) {
    out.errors.push({ key: "pdf", text: t("Erzeuge zuerst deine Rechnung als PDF oder lade deine eigene Datei hoch.", "First create your invoice as a PDF or upload your own file.") });
  }
  // Eigene Datei: Nummer und Betrag bestätigen, sonst lehnt die Datenbank das Einreichen ab (0045)
  if (stage === "submit" && env.hasPdf && env.pdfOrigin === "uploaded" && !env.pdfConfirmed) {
    out.errors.push({ key: "pdf-confirm", text: t("Bestätige, dass Nummer und Bruttobetrag deiner hochgeladenen Datei mit deinen Angaben übereinstimmen.",
      "Confirm that the number and gross amount of your uploaded file match your details.") });
  }
  // Auslagen nur mit Beleg (Hotel, Fahrt …)
  if (stage === "submit" && purpose === "expenses" && !env.hasReceipt) {
    out.errors.push({ key: "receipt", text: t("Für Auslagen brauchst du einen Beleg. Hänge ihn im Schritt „Datei“ an.", "Expenses need a receipt. Attach it in the “File” step.") });
  }
  return out;
}

/** Schlüssel der Rückfrage „steuerliche Behandlung bestätigen“ (0181); bestätigt → invoices.tax_treatment_confirmed_at beim Einreichen */
export const TAX_CONFIRM_KEY = "tax-treatment";

/** Rückfragen, die noch nicht bestätigt sind. */
export function openQuestions(check: DraftCheck, confirmed: ReadonlySet<string>): Message[] {
  return check.questions.filter((q) => !confirmed.has(q.key));
}

/** Kopf der Rechnung für die Datenbank (nur Spalten, die ein Talent schreiben darf). */
export function invoiceHeader(d: InvoiceDraft, comp: DraftComputation, scope: { orgId: string; talentId: string; direct?: boolean; brandId?: string | null }): Record<string, unknown> {
  const due = d.termDays !== null ? dueDateFrom(d.issueDate, d.termDays) : null;
  return {
    org_id: scope.orgId,
    // Direkte Abrechnung: Rechnung an die Marke (0039); sonst an die eigene Agentur
    kind: scope.direct ? "talent_to_brand" : "talent_to_agency",
    ...(scope.direct ? { brand_id: scope.brandId ?? null } : {}),
    deal_id: d.purpose === "other" ? null : d.dealId,
    // Nachberechnung und Auslagen hängen an der Kooperation, nicht an einem Monat (Befund 18)
    period_id: (d.purpose ?? "month") === "month" ? d.periodId : null,
    // Ohne Kooperation (PARITAET-5): Beschreibung ist Pflicht; sonst optional
    talent_note: d.note?.trim() ? d.note.trim().slice(0, 1000) : null,
    talent_id: scope.talentId,
    number: d.number.trim(),
    issue_date: d.issueDate,
    service_period_start: d.serviceFrom,
    service_period_end: d.serviceTo,
    revenue_month: (d.purpose ?? "month") === "month" ? (d.month ? `${d.month}-01` : null) : (d.serviceTo ?? d.issueDate).slice(0, 7) + "-01",
    currency: d.currency,
    net_cents: comp.totals.netCents,
    tax_cents: comp.totals.vatCents,
    gross_cents: comp.totals.grossCents,
    tax_mode: TAX_MODE_DB[comp.tax.mode],
    notice_key: comp.tax.noticeKey,
    document_due_date: due,
    effective_due_date: due,
    issuer_due_date: due,
    // Satz des Provisionsabzugs (0161, AD-218): Grundlage der Prüfung von Rechnungen ohne Monat. Im Anteil-Modus (Beträge schon
    // nach Abzug, auch beim erneuten Bearbeiten einer gespeicherten Rechnung) bleibt der gespeicherte Satz unverändert
    ...(d.mode === "budget" && d.commissionPercent != null ? { commission_bp_applied: Math.round(comp.commissionPercent * 100) } : {}),
  };
}

/** Positionen für invoice_lines: Einzelpreis nach Provision, Summe je Position (ST-18). */
export function invoiceLineRows(comp: DraftComputation, orgId: string, invoiceId: string): Record<string, unknown>[] {
  const bp = Math.round(comp.tax.ratePercent * 100);
  return comp.totals.lines.map((l, i) => ({
    org_id: orgId,
    invoice_id: invoiceId,
    position: i + 1,
    description: l.description.trim(),
    quantity: l.quantity,
    unit_cents: l.unitPriceCents,
    // Abnahme E-2: Satz je Position
    tax_rate_bp: comp.lineRatesBp?.[i] ?? bp,
    net_cents: l.lineTotalCents,
    commission_free: l.commissionFree,
  }));
}

let lineSeq = 0;
export const newLineKey = () => `l${++lineSeq}`;

/** Vorschläge der Agentur aus der Anforderung (deal_periods.suggested_items); die Form ist offen, gelesen wird großzügig. */
export function parseSuggestedItems(raw: unknown): DraftLine[] {
  if (!Array.isArray(raw)) return [];
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
  return raw.flatMap((x): DraftLine[] => {
    if (!x || typeof x !== "object") return [];
    const o = x as Record<string, unknown>;
    const description = [o.description, o.name, o.title, o.label].find((v) => typeof v === "string" && v.trim() !== "") as string | undefined;
    if (!description) return [];
    const quantity = num(o.quantity ?? o.qty) ?? 1;
    const unit = num(o.unit_cents ?? o.unitPriceCents ?? o.unit_price_cents ?? o.price_cents ?? o.amount_cents ?? o.default_price_cents) ?? 0;
    const commissionFree = o.commission_free === true || o.commissionFree === true || o.commissionable === false;
    return [{ key: newLineKey(), description: description.trim(), quantity: quantity > 0 ? quantity : 1, unitCents: Math.max(0, Math.round(unit)), commissionFree }];
  });
}

/** Startpositionen: Vorschläge der Agentur, sonst Monatsbetrag und Zusatzpositionen der Kooperation (Beschreibung in der Belegsprache). */
export function defaultLines(deal: CDeal, period: CPeriod, extras: readonly CLineItem[], brandName: string, lang: Lang = "de"): DraftLine[] {
  const suggested = parseSuggestedItems(period.suggested_items);
  if (suggested.length) return suggested;
  return budgetLines(deal, period, extras, brandName, lang);
}

function budgetLines(deal: CDeal, period: CPeriod, extras: readonly CLineItem[], brandName: string, lang: Lang): DraftLine[] {
  const month = monthOf(period.month.slice(0, 7), lang);
  const lines: DraftLine[] = [];
  if (deal.model !== "commission_only" && (period.amount_cents ?? 0) > 0) {
    const what = deal.deliverables?.trim() ? deal.deliverables.trim() : deal.title;
    lines.push({ key: newLineKey(), description: `${what} · ${brandName} · ${month}`, quantity: 1, unitCents: period.amount_cents ?? 0, commissionFree: false });
  }
  for (const e of extras) lines.push({ key: newLineKey(), description: e.description, quantity: 1, unitCents: e.amount_cents, commissionFree: !e.commissionable });
  return lines;
}

/**
 * Startstand der Positionen eines Monats samt Eingabemodus (GELD-1): Die Vorschläge der Agentur sind schon dein Anteil (bzw. im
 * Direktweg der Betrag an die Marke) und werden ohne weiteren Abzug übernommen („share“). Ohne Vorschläge: direkt an die Marke der
 * Betrag nach dem Abzug der Marke; über die Agentur mit Staffel oder Abzug der Marke dein Anteil nach derselben Regel wie die Agentur,
 * sonst das Budget mit dem vereinbarten Satz („budget“, TA-083).
 */
export function draftStart(deal: CDeal, period: CPeriod, extras: readonly CLineItem[], brandName: string, lang: Lang, rate: CommissionInfo | null):
  Pick<InvoiceDraft, "lines" | "mode" | "commissionPercent"> {
  const suggested = parseSuggestedItems(period.suggested_items);
  if (suggested.length) return { lines: suggested, mode: "share", commissionPercent: null };
  const lines = budgetLines(deal, period, extras, brandName, lang);
  // Die Agentur zeigt nur den Anteil (0079): die Beträge sind schon nach Provision, nichts mehr abziehen
  if (amountsAreShare(deal) && !isDirect(deal)) return { lines, mode: "share", commissionPercent: null };
  const asShare = (amounts: readonly { amountCents: number }[]) => lines.map((l, i) => ({ ...l, quantity: 1, unitCents: amounts[i]!.amountCents }));
  const posten = lines.map((l) => ({ amountCents: Math.round(l.unitCents * l.quantity), commissionFree: l.commissionFree }));
  if (isDirect(deal)) return { lines: asShare(brandNetLines(posten, brandDiscountFromRow(deal))), mode: "share", commissionPercent: null };
  if (rate && (brandDiscountFromRow(deal) || rateWithTiers(deal, rate, period.month).tiers)) {
    return { lines: asShare(creatorShareLines(posten, rateWithTiers(deal, rate, period.month), brandDiscountFromRow(deal))), mode: "share", commissionPercent: null };
  }
  return { lines, mode: "budget", commissionPercent: rate?.percent ?? null };
}

/** Nächste Rechnungsnummer aus der letzten eigenen Nummer (Zähler +1, Jahr wie das Rechnungsdatum). */
export function suggestNextNumber(recent: readonly (string | null)[], issueDate: IsoDate): string {
  const last = recent.find((n) => !!n && /\d+\s*$/.test(n));
  if (!last) return "";
  const year = issueDate.slice(0, 4);
  const m = /^(.*?)(\d+)(\s*)$/.exec(last);
  if (!m) return "";
  let prefix = m[1] ?? "";
  const digits = m[2] ?? "";
  const yearInPrefix = /(20\d{2})(?!.*20\d{2})/.exec(prefix);
  let next = Number(digits) + 1;
  if (yearInPrefix && yearInPrefix[1] !== year) {
    prefix = prefix.slice(0, yearInPrefix.index) + year + prefix.slice(yearInPrefix.index + 4);
    next = 1;
  }
  return `${prefix}${String(next).padStart(digits.length, "0")}`;
}

/** Zahlungsziel der letzten eigenen Rechnung (Fälligkeit − Rechnungsdatum), sonst null. */
export function lastTermDays(invoices: readonly CInvoice[]): number | null {
  for (const i of invoices) {
    if (i.issue_date && i.issuer_due_date) {
      const d = diffDays(i.issue_date, i.issuer_due_date);
      if (d >= 0 && d <= 180) return d;
    }
  }
  return null;
}

/** Leistungszeitraum-Vorschlag: Monatsanfang bis Monatsende, höchstens bis heute. */
export function defaultServicePeriod(month: MonthKey, today: IsoDate): { from: IsoDate; to: IsoDate } {
  const from = `${month}-01`;
  const end = lastDayOfMonth(month);
  return { from, to: end > today && from <= today ? today : end };
}

/** Rechnung → Entwurf im Assistenten (Beträge sind die eigenen, also ohne weiteren Abzug). */
export function draftFromInvoice(i: CInvoice, lines: readonly { description: string; quantity: number; unit_cents: number; commission_free: boolean; tax_rate_bp?: number | null }[]): InvoiceDraft {
  const all = lines.length > 0 && lines.every((l) => l.commission_free);
  return {
    invoiceId: i.id,
    // Ohne Monat: Nachberechnung, bzw. Auslagen, wenn alle Positionen provisionsfrei sind
    purpose: !i.deal_id ? "other" : i.period_id ? "month" : all ? "expenses" : "extra",
    dealId: i.deal_id,
    note: i.talent_note ?? "",
    periodId: i.period_id,
    month: i.revenue_month ? i.revenue_month.slice(0, 7) : null,
    number: i.number ?? "",
    // „heute“ in der Zeitzone des Kontos, nicht in UTC (Gesamtprüfung 07.10.2026: nach 22/23 Uhr stand sonst der Vortag)
    issueDate: i.issue_date ?? heuteLokal(),
    serviceFrom: i.service_period_start,
    serviceTo: i.service_period_end,
    termDays: i.issue_date && i.issuer_due_date ? diffDays(i.issue_date, i.issuer_due_date) : null,
    mode: "share",
    commissionPercent: null,
    // Abnahme E-2: gespeicherter Satz je Position bleibt erhalten (ohne Steuerausweis gilt ohnehin 0)
    lines: lines.map((l) => ({ key: newLineKey(), description: l.description, quantity: Number(l.quantity), unitCents: l.unit_cents, commissionFree: l.commission_free,
      ...(Number(i.tax_cents) !== 0 && l.tax_rate_bp != null ? { rateBp: Number(l.tax_rate_bp) } : {}) })),
    currency: i.currency,
  };
}

/** Dubletten gleicher Leistung (RE-40) für die Rechnungsliste: Rechnung → Gegenrechnungen. */
export function sameServiceDuplicates(views: readonly TalentInvoiceView[]): Map<string, string[]> {
  return findSameServiceDuplicates(views.map((v) => v.record));
}

// ── CSV (TA-034, TA-050) ────────────────────────────────────────────────────

/** CSV mit Semikolon, BOM und Schutz vor Formeln (=, +, -, @ am Anfang; reine Zahlen wie „-12,00“ bleiben Zahlen, csvCellGuard). */
export function toCsv(rows: readonly (readonly (string | number | null | undefined)[])[]): string {
  const cell = (v: string | number | null | undefined) => {
    let s = v === null || v === undefined ? "" : String(v);
    s = csvCellGuard(s);
    return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return `﻿${rows.map((r) => r.map(cell).join(";")).join("\r\n")}`;
}

/** Cent als Dezimalzahl mit Komma für CSV („2800,00“). */
/** Betrag für CSV mit dem Dezimalzeichen der Oberfläche (in der Schweiz Punkt), wie Kennzahlen und Datenexport (SAAS-R3-3) */
export function csvAmount(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  const neg = cents < 0;
  const a = Math.abs(cents);
  return `${neg ? "-" : ""}${Math.floor(a / 100)}${decimalSeparator()}${String(a % 100).padStart(2, "0")}`;
}


// ── Neuer Entwurf ───────────────────────────────────────────────────────────

/** Rechnung möglich: nicht bei Event und Gifting, Barter nur mit Budget (0016). */
export function invoiceable(d: CDeal): boolean {
  return !(NO_REVENUE_MODELS.has(d.model) && !(d.model === "barter" && (d.budget_cents ?? 0) > 0));
}

/**
 * Auswahl im Assistenten (Befund 26): offene und angeforderte Kooperationen zuerst, abgeschlossene getrennt; die
 * Beschriftung trägt Startmonat und Nummer, damit gleichnamige Kooperationen unterscheidbar sind.
 */
export function dealChoices(b: Pick<CreatorBase, "deals" | "periods" | "invoices" | "brands">, lang: Lang = "de"): { open: { deal: CDeal; label: string }[]; done: { deal: CDeal; label: string }[] } {
  const label = (d: CDeal) => [
    `${b.brands.get(d.brand_id)?.name ?? tr(lang, "Marke", "Brand")} · ${d.title}`,
    d.start_month ? monthOf(d.start_month.slice(0, 7), lang) : null,
    d.deal_number != null ? `${tr(lang, "Nr.", "No.")} ${d.deal_number}` : null,
  ].filter(Boolean).join(" · ");
  const requested = new Set(b.periods.filter((p) => p.invoice_requested_at && !b.invoices.some((i) => i.period_id === p.id && !["withdrawn", "discarded", "cancelled"].includes(i.status))).map((p) => p.deal_id));
  const deals = b.deals.filter(invoiceable);
  const isDone = (d: CDeal) => d.status === "done" || d.status === "cancelled";
  const byStart = (x: CDeal, y: CDeal) => (y.start_month ?? "").localeCompare(x.start_month ?? "");
  const open = deals.filter((d) => !isDone(d)).sort((x, y) => Number(requested.has(y.id)) - Number(requested.has(x.id)) || byStart(x, y));
  const done = deals.filter(isDone).sort(byStart);
  return { open: open.map((deal) => ({ deal, label: label(deal) })), done: done.map((deal) => ({ deal, label: label(deal) })) };
}

/** Eigene Nummern für den Vorschlag der nächsten Nummer: alle Agenturen, neueste zuerst (Befund 10). */
export function recentNumbers(b: Pick<CreatorBase, "invoices"> & { ownNumbers?: readonly OwnNumber[] }): string[] {
  const all = [
    ...(b.ownNumbers ?? []).map((n) => ({ number: n.number, date: n.issue_date ?? "" })),
    ...b.invoices.filter((i) => !["withdrawn", "discarded"].includes(i.status)).map((i) => ({ number: i.number, date: i.issue_date ?? i.created_at.slice(0, 10) })),
  ];
  return all.filter((x): x is { number: string; date: string } => !!x.number).sort((x, y) => y.date.localeCompare(x.date)).map((x) => x.number);
}

/** Neuer Entwurf: Kooperation und Monat aus dem Link, sonst der angeforderte Monat, sonst der laufende. */
export function newDraft(b: CreatorBase, today: string, dealParam: string | null, monthParam: string | null, lang: Lang = "de", wanted: DraftPurpose = "month"): InvoiceDraft {
  // Gutschriftverfahren vereinbart: Monate rechnet die Agentur ab, eigene Rechnungen nur für Nachberechnung/Auslagen (Befund Creator 11)
  const purpose: DraftPurpose = wanted === "month" && b.talent.self_billing_agreed_on ? "extra" : wanted;
  const deals = b.deals.filter(invoiceable);
  const requested = b.periods
    .filter((p) => p.invoice_requested_at && !b.invoices.some((i) => i.period_id === p.id && !["withdrawn", "discarded", "cancelled"].includes(i.status)))
    .sort((x, y) => x.month.localeCompare(y.month));
  // Befund Creator 8: ohne Link die Kooperation mit angefordertem, sonst mit ältestem geliefertem Monat ohne Rechnung
  const delivered = b.periods.filter((p) => deliveredOpen(p, b, today)).sort((x, y) => x.month.localeCompare(y.month));
  const deal = deals.find((d) => d.id === dealParam) ?? deals.find((d) => requested.some((p) => p.deal_id === d.id))
    ?? deals.find((d) => delivered.some((p) => p.deal_id === d.id)) ?? deals[0] ?? null;
  const periods = deal ? b.periods.filter((p) => p.deal_id === deal.id).sort((x, y) => x.month.localeCompare(y.month)) : [];
  const period = pickPeriod(periods, b, today, monthParam);
  const extras = period ? extrasOfPeriod(b.lineItems, b.periods, period) : [];
  const month = period?.month.slice(0, 7) ?? null;
  const service = month ? defaultServicePeriod(month, today) : null;
  const rate = deal ? rateOf(b, deal) : null;
  const termDays = typeof b.org.talent_payment_terms_days === "number" ? b.org.talent_payment_terms_days : lastTermDays(b.invoices);
  if (purpose === "other") {
    // Sonstige Rechnung an die Agentur ohne Kooperation (PARITAET-5): eigener Betrag, keine Provision abzuziehen
    return {
      invoiceId: null, purpose: "other", dealId: null, periodId: null, month: null, note: "",
      number: suggestNextNumber(recentNumbers(b), today), issueDate: today, serviceFrom: today, serviceTo: today, termDays,
      mode: "share", commissionPercent: null, lines: [{ key: newLineKey(), description: "", quantity: 1, unitCents: 0, commissionFree: false }],
      currency: b.org.currency,
    };
  }
  if (purpose !== "month" || (deal && periods.length === 0)) {
    // Nachberechnung bzw. Auslagen ohne Monat (Befund 18); auch eine Kooperation ohne Monate lässt sich so abrechnen
    const p: DraftPurpose = purpose === "month" ? "extra" : purpose;
    const full = p === "expenses" || (!!deal && invoicesWithoutDeduction(deal));
    return {
      invoiceId: null, purpose: p, dealId: deal?.id ?? null, periodId: null, month: null,
      number: suggestNextNumber(recentNumbers(b), today), issueDate: today, serviceFrom: today, serviceTo: today, termDays,
      mode: full ? "share" : "budget", commissionPercent: full ? null : rate?.percent ?? null,
      lines: [{ key: newLineKey(), description: "", quantity: 1, unitCents: 0, commissionFree: p === "expenses" }],
      currency: deal?.currency ?? b.org.currency,
    };
  }
  return {
    invoiceId: null,
    purpose: "month",
    dealId: deal?.id ?? null,
    periodId: period?.id ?? null,
    month,
    number: suggestNextNumber(recentNumbers(b), today),
    issueDate: today,
    serviceFrom: service?.from ?? null,
    serviceTo: service?.to ?? null,
    // Zahlungsziel der Agentur für Creator-Rechnungen, sonst das der letzten eigenen Rechnung
    termDays,
    // Vorschläge der Agentur und der Direktweg ohne Abzug („share“), sonst Budget mit Satz (draftStart, GELD-1)
    ...(deal && period ? draftStart(deal, period, extras, b.brands.get(deal.brand_id)?.name ?? tr(lang, "Marke", "Brand"), lang, rateOf(b, deal, period.month))
      : { mode: deal && isDirect(deal) ? "share" as const : "budget" as const, commissionPercent: deal && isDirect(deal) ? null : rate?.percent ?? null, lines: [] }),
    currency: deal?.currency ?? b.org.currency,
  };
}

/** Arbeitsstände, ab denen der Content eines Monats als geliefert gilt */
const CONTENT_DELIVERED = new Set(["published", "reporting", "invoicing", "done"]);
const periodOpen = (p: CPeriod, b: Pick<CreatorBase, "invoices">) =>
  p.status !== "cancelled" && !b.invoices.some((i) => i.period_id === p.id && !["withdrawn", "discarded", "cancelled"].includes(i.status));
/** Geliefert und noch ohne Rechnung: Arbeitsstand „veröffentlicht“ oder später, sonst ein abgelaufener Monat */
function deliveredOpen(p: CPeriod, b: Pick<CreatorBase, "invoices">, today: string): boolean {
  if (!periodOpen(p, b) || p.manual_status === "cancelled" || p.manual_status === "blocked") return false;
  return CONTENT_DELIVERED.has(p.manual_status ?? "") || p.month.slice(0, 7) < today.slice(0, 7);
}

/**
 * Vorgewählter Monat (Befund Creator 8): der Monat aus dem Link, sonst der angeforderte, sonst der älteste gelieferte
 * ohne Rechnung, sonst der laufende. Künftige Monate nie von selbst, nur auf ausdrückliche Wahl.
 */
export function pickPeriod(periods: readonly CPeriod[], b: CreatorBase, today: string, monthParam: string | null): CPeriod | null {
  const open = (p: CPeriod) => periodOpen(p, b);
  const cur = today.slice(0, 7);
  return periods.find((p) => p.month.slice(0, 7) === monthParam)
    ?? periods.find((p) => p.invoice_requested_at && open(p))
    ?? periods.find((p) => deliveredOpen(p, b, today))
    ?? periods.find((p) => p.month.slice(0, 7) === cur && open(p))
    ?? [...periods].reverse().find((p) => p.month.slice(0, 7) <= cur && open(p))
    ?? null;
}

// ── Steuerstatus rückwirkend (CREATOR-5) ───────────────────────────────────

const RETRO_OPEN = new Set(["draft", "submitted", "revision_requested", "rejected"]);
const RETRO_FINAL = new Set(["approved", "sent", "paid"]);

/**
 * Eigene Rechnungen ab dem Gilt-ab-Datum, die zum neuen Steuerstatus nicht mehr passen: mit Umsatzsteuer trotz
 * Kleinunternehmerregelung bzw. ohne trotz Regelbesteuerung. Offene lassen sich korrigieren oder zurückziehen,
 * freigegebene und bezahlte brauchen eine Korrekturrechnung (die Agentur bekommt dazu eine Aufgabe, 0079).
 */
export function retroTaxConflicts(invoices: readonly Pick<CInvoice, "id" | "number" | "status" | "issue_date" | "tax_mode" | "tax_cents" | "kind">[],
  smallBusiness: boolean, validFrom: IsoDate): { open: typeof invoices[number][]; final: typeof invoices[number][] } {
  const hit = invoices.filter((i) => (i.kind === "talent_to_agency" || i.kind === "talent_to_brand") && !!i.issue_date && i.issue_date >= validFrom
    && (smallBusiness ? (i.tax_cents ?? 0) !== 0 : i.tax_mode === "small_business"))
    .sort((a, b) => (a.issue_date ?? "").localeCompare(b.issue_date ?? ""));
  return { open: hit.filter((i) => RETRO_OPEN.has(i.status)), final: hit.filter((i) => RETRO_FINAL.has(i.status)) };
}

// ── Positionstext ausformulieren (PARITAET-R3-5, „Zauberstab“ des bisherigen Rechnungsgenerators) ─────────────────────

export interface LineSuggestionInput {
  /** bisheriger Text der Position, z. B. „2x Stories“; leer: Umfang der Kooperation */
  text: string;
  quantity: number;
  deal: Pick<CDeal, "title" | "deliverables" | "platforms" | "usage_rights" | "usage_rights_until"> | null;
  brandName: string | null;
  /** Leistungsmonat 'YYYY-MM' oder 'YYYY-MM-DD' (Monatsrechnung) */
  month: string | null;
  serviceFrom: string | null;
  serviceTo: string | null;
  /** Social-Handles aus dem Profil (TA-145: „für die Leistungsbeschreibung auf Rechnungen“) */
  handles: { instagram?: string | null; tiktok?: string | null; youtube?: string | null };
  /** Sprache des Belegs (nicht der Oberfläche) */
  lang: Lang;
}

type SocialPlatform = "instagram" | "tiktok" | "youtube";
const SOCIAL_NAME: Record<SocialPlatform, string> = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube" };

/** Handle mit @, ohne Leerzeichen und ohne Adresse davor („https://instagram.com/mara“ → „@mara“). */
export function handleText(raw: string | null | undefined): string | null {
  const h = (raw ?? "").trim().replace(/^https?:\/\/(www\.)?[^/]+\//i, "").replace(/[/?#].*$/, "").replace(/^@+/, "").trim();
  return h && /^[\w.\-]{1,60}$/.test(h) ? `@${h}` : null;
}

/** Tag als Datum im Format der Belegsprache (nicht im eigenen Anzeigeformat der Person). */
function docDay(iso: string, lang: Lang): string {
  const [y = "", m = "", d = ""] = iso.slice(0, 10).split("-");
  return lang === "en" ? `${d}/${m}/${y}` : `${d}.${m}.${y}`;
}

/**
 * Macht aus einer Kurzangabe eine vollständige Rechnungszeile mit Art und Umfang der Leistung: Menge („2x“), Format und
 * Plattform (Story, Reel, Beitrag, Video, Nutzungsrechte), Handle aus dem Profil, Marke und Leistungszeitraum. Unbekannte
 * Angaben bleiben als Text stehen und bekommen nur Marke und Zeitraum dazu. Mehrfaches Anwenden ändert nichts mehr.
 */
export function suggestLine(i: LineSuggestionInput): { description: string; quantity: number } {
  const L = (de: string, en: string) => tr(i.lang, de, en);
  let text = i.text.trim().replace(/\s+/g, " ");
  if (!text) text = (i.deal?.deliverables ?? "").split(/\r?\n/).map((x) => x.trim()).find(Boolean) ?? i.deal?.title ?? "";
  let quantity = i.quantity > 0 ? i.quantity : 1;
  // schon ausformuliert (Marke oder Zeitraum steht drin): nichts mehr ändern
  if (text.includes(" · ")) return { description: text, quantity };
  // Kurzangabe („2x Stories“, „Reel“): Menge und Format erkennen; längere Beschreibungen („2 Reels + 3 Stories“) bleiben stehen
  const simple = text.split(" ").length <= 4 && !/[+,;&/]|\b(und|and)\b/i.test(text);
  const q = simple ? /^(\d{1,4}(?:[.,]\d+)?)\s*(?:x|×|\*|mal)?\s+(.+)$/i.exec(text) ?? /^(\d{1,4})\s*(?:x|×)\s*(.+)$/i.exec(text) : null;
  // Gegenprüfung GB-4: Menge über parseQuantityInput; eine unlesbare oder 0 wird nicht übernommen (Text bleibt, wie er ist)
  const n = q ? parseQuantityInput(q[1]!) : null;
  if (q && n !== null && Number.isFinite(n) && n > 0) {
    quantity = n;
    text = q[2]!.trim();
  }
  const many = quantity !== 1;
  const lower = text.toLowerCase();
  const dealSocial = (i.deal?.platforms ?? []).filter((p): p is SocialPlatform => p === "instagram" || p === "tiktok" || p === "youtube");
  const named = (["tiktok", "youtube", "instagram"] as const).filter((p) =>
    p === "tiktok" ? /tik\s?tok/.test(lower) : p === "youtube" ? /youtube|\bshorts?\b/.test(lower) : /insta|\bstor(y|ys|ies)\b|\breels?\b/.test(lower));
  let platform: SocialPlatform | null = named.length === 1 ? named[0]! : named.length === 0 && dealSocial.length === 1 ? dealSocial[0]! : null;
  let label: string | null = null;
  const pair = (p: SocialPlatform, deOne: string, deMany: string, enOne: string, enMany: string) =>
    many ? L(`${SOCIAL_NAME[p]}-${deMany}`, `${SOCIAL_NAME[p]} ${enMany}`) : L(`${SOCIAL_NAME[p]}-${deOne}`, `${SOCIAL_NAME[p]} ${enOne}`);
  if (simple) {
    if (/\bstor(y|ys|ies)\b/.test(lower)) { platform = "instagram"; label = pair("instagram", "Story", "Storys", "story", "stories"); }
    else if (/\breels?\b/.test(lower)) { platform = "instagram"; label = pair("instagram", "Reel", "Reels", "reel", "reels"); }
    else if (/\bshorts?\b/.test(lower)) { platform = "youtube"; label = many ? "YouTube Shorts" : "YouTube Short"; }
    else if (/\b(videos?|clips?|tiktoks?)\b/.test(lower) && platform) label = pair(platform, "Video", "Videos", "video", "videos");
    else if (/\b(posts?|beitr(a|ä)ge?|feed)\b/.test(lower)) { platform ??= "instagram"; label = pair(platform, "Beitrag", "Beiträge", "post", "posts"); }
    else if (/\bugc\b/.test(lower)) {
      platform = null;
      label = many ? L("UGC-Videos (ohne Veröffentlichung auf dem eigenen Kanal)", "UGC videos (not published on own channel)")
        : L("UGC-Video (ohne Veröffentlichung auf dem eigenen Kanal)", "UGC video (not published on own channel)");
    }
  }
  if (/nutzungsrecht|usage|lizenz|licen[cs]e/.test(lower)) {
    platform = null;
    if (simple) {
      const until = i.deal?.usage_rights_until ? L(` bis ${docDay(i.deal.usage_rights_until, "de")}`, ` until ${docDay(i.deal.usage_rights_until, "en")}`) : "";
      const scope = i.deal?.usage_rights?.trim() ? ` (${i.deal.usage_rights.trim()})` : "";
      label = `${L("Nutzungsrechte", "Usage rights")}${scope}${until}`;
    }
  }
  let out = label ?? text;
  const handle = platform ? handleText(i.handles[platform]) : null;
  if (handle && !out.includes(handle)) out += ` (${handle})`;
  const period = i.month ? monthOf(i.month.slice(0, 7), i.lang)
    : i.serviceFrom && i.serviceTo ? (i.serviceFrom === i.serviceTo ? docDay(i.serviceFrom, i.lang) : `${docDay(i.serviceFrom, i.lang)} – ${docDay(i.serviceTo, i.lang)}`)
      : i.serviceFrom ? docDay(i.serviceFrom, i.lang) : null;
  const tail = [i.brandName && !out.includes(i.brandName) ? i.brandName : null, period && !out.includes(period) ? period : null].filter(Boolean);
  if (tail.length) out = `${out} · ${tail.join(" · ")}`;
  return { description: out.slice(0, 500), quantity };
}
